<template>
  <div class="container-fluid multi-account-dashboard">
    <!-- 머리말 + 영역 바로가기 -->
    <div class="row">
      <div class="col-lg-12">
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div>
            <h4 class="mb-1">다계정 통합 현황</h4>
            <p class="mb-0 text-muted">
              연결된 계정을 한 화면에서 비교합니다. 계정마다 쓰는 서비스가 달라, 그 계정이 쓰는 영역에만 줄이 생깁니다.
            </p>
          </div>
          <div class="d-flex align-items-center">
            <span v-if="switchingEmail" class="mr-2 text-primary small">계정 전환 중…</span>
            <!--
              force=true 로 부른다. 인자 없이 @click="refreshAll" 로 두면 Vue 가 MouseEvent 를
              첫 인자로 넘겨 우연히 truthy 가 되는데, 그건 의도가 아니라 사고다 — 명시한다.
            -->
            <button
              type="button"
              class="btn btn-primary"
              :disabled="loading || !!switchingEmail"
              @click="refreshAll(true)"
            >
              전체 새로고침
            </button>
          </div>
        </div>
      </div>

      <!--
        내 자산 — 이 화면에서 가장 먼저 보는 숫자 두 개(현금자산·재고자산 지난달 대비).
        계산은 판매장부 「자산현황」 차트와 같은 함수라 두 화면 숫자가 같다.
        판매장부를 쓸 수 있는 계정(로켓그로스·SCM)에만 그린다.
      -->
      <div class="col-lg-12" v-if="canSeeAssetTiles">
        <AssetChangeTiles />
      </div>

      <!-- 표 읽는 법. 아래 모든 표가 같은 다섯 상태를 쓴다 -->
      <div class="col-lg-12" v-if="visibleAreas.length">
        <b-card class="mb-3 multi-account-dashboard__nav-card">
          <div class="multi-account-dashboard__legend">
            <span class="multi-account-dashboard__legend-item">
              <span class="multi-account-dashboard__cell-value multi-account-dashboard__cell-value--zero">0</span>
              정상(0건)
            </span>
            <span class="multi-account-dashboard__legend-item">
              <span class="multi-account-dashboard__cell-empty">—</span>
              값 없음
            </span>
            <span class="multi-account-dashboard__legend-item">
              <span class="multi-account-dashboard__cell-error">조회실패</span>
              불러오지 못함
            </span>
            <span class="multi-account-dashboard__legend-item">
              <span class="multi-account-dashboard__cell-need">수집 필요 ›</span>
              아직 수집하지 않음(오류 아님) — 누르면 그 계정 화면으로 이동
            </span>
            <!-- 다섯 번째 상태. 「값 없음」과 달리 그 계정이 애초에 안 쓰는 영역이다 -->
            <span class="multi-account-dashboard__legend-item">
              <span class="multi-account-dashboard__cell-na">해당 없음</span>
              그 계정이 쓰지 않는 영역
            </span>
          </div>
        </b-card>
      </div>
    </div>

    <!--
      영역별 진행 표시. 8개 영역을 **동시에** 부르고 먼저 온 것부터 채우는데, 그게 화면에
      보이지 않아 첫 방문 7초 내내 멈춘 것처럼 느껴졌다(병목은 로켓배송 판매장부).
      어디까지 왔는지 + 무엇이 남았는지를 같이 보여준다. 다 오면 저절로 사라진다.
    -->
    <div class="row" v-if="isLoadingMetrics">
      <div class="col-lg-12">
        <div class="multi-account-dashboard__progress" role="status" aria-live="polite">
          <span class="multi-account-dashboard__spinner" aria-hidden="true"></span>
          <span class="multi-account-dashboard__progress-text">
            불러오는 중 — {{ loadedAreaCount }}/{{ totalAreaCount }} 영역 완료
            <span v-if="loadingAreaLabels" class="multi-account-dashboard__progress-rest">({{ loadingAreaLabels }} 남음)</span>
          </span>
          <span class="multi-account-dashboard__progress-bar" aria-hidden="true">
            <span class="multi-account-dashboard__progress-fill" :style="{ width: progressPercent + '%' }"></span>
          </span>
        </div>
      </div>
    </div>

    <!-- 계정 목록 로딩/실패 -->
    <div class="row" v-if="loading">
      <div class="col-lg-12">
        <b-card><p class="mb-0 text-muted">계정 정보를 불러오는 중입니다…</p></b-card>
      </div>
    </div>
    <div class="row" v-else-if="loadError">
      <div class="col-lg-12">
        <b-card>
          <p class="mb-2 text-danger">{{ loadError }}</p>
          <button type="button" class="btn btn-outline-primary btn-sm" @click="refreshAll(true)">다시 시도</button>
        </b-card>
      </div>
    </div>
    <div class="row" v-else-if="!visibleAreas.length">
      <div class="col-lg-12">
        <b-card><p class="mb-0 text-muted">표시할 계정이 없습니다. 헤더의 계정 전환 메뉴에서 계정을 먼저 연결해 주세요.</p></b-card>
      </div>
    </div>

    <!-- 영역별 비교표 -->
    <div class="row" v-else>
      <!--
        계정별 한 줄 요약. 표를 대신하는 게 아니라 표 위에 얹는다 —
        아래 비교표가 계정별 전체 지표를 계속 책임진다.
        여기에 올릴 지표는 SUMMARY_METRICS 하나만 고치면 늘어난다.
      -->
      <!--
        올릴 값이 하나도 없는 계정(장부가 잠겼거나 그 영역을 안 쓰는 계정)은 줄을 만들지 않는다.
        라벨만 남고 값이 비면 요약이 아니라 잡음이다. 전부 비면 카드째 숨긴다.
      -->
      <!--
        「지금 볼 것」 — 이 화면의 진입점. 손봐야 할 칸만 모아 맨 위에 둔다.
        누르면 그 영역으로 데려간다. 없으면 카드째 안 그린다(빈 칸은 잡음이다).
      -->
      <div class="col-lg-12" v-if="attentionChips.length || growthRiskGroups.length">
        <b-card class="mb-3 multi-account-dashboard__attention-card">
          <div class="multi-account-dashboard__attention" v-if="attentionChips.length">
            <span class="multi-account-dashboard__attention-title">
              지금 볼 것 <b>{{ attentionChips.length }}</b>
            </span>
            <button
              v-for="(chip, i) in attentionShown"
              :key="'att-' + chip.areaKey + '-' + chip.email + '-' + i"
              type="button"
              class="multi-account-dashboard__attention-chip"
              :class="'multi-account-dashboard__attention-chip--' + chip.kind"
              :title="chip.account + ' · ' + chip.areaLabel + ' · ' + chip.text + ' — 누르면 그 표로 이동합니다'"
              @click="scrollToBlock(chip.areaKey)"
            >
              <span class="multi-account-dashboard__attention-acc">{{ chip.account }}</span>
              <span class="multi-account-dashboard__attention-area">{{ chipAreaLabel(chip.areaLabel) }}</span>
              <span>{{ chip.text }}</span>
            </button>
            <span v-if="attentionRest" class="multi-account-dashboard__attention-rest">외 {{ attentionRest }}개</span>
          </div>

          <!--
            로켓그로스 리스크 타일 — 지표마다 「몇 개 계정이 걸렸나」. 누르면 아래 통합현황 표를
            그 지표가 있는 계정만 남기고 큰 순서로 세운다. 다시 누르거나 「해제」로 원복.
            ⚠ 로딩·실패·수집 전 계정은 세지 않는다(모르는 것을 0 으로 세면 안 된다) ⇒ 「집계 N/M」.
          -->
          <div v-if="growthRiskGroups.length" class="mad-risk">
            <div class="mad-risk__head">
              <span class="multi-account-dashboard__attention-title">로켓그로스 리스크</span>
              <span class="mad-risk__legend">
                <span class="mad-risk__tag mad-risk__tag--alert"><i class="fas fa-exclamation-circle" aria-hidden="true"></i> 위험</span>
                <span class="mad-risk__tag mad-risk__tag--warn"><i class="fas fa-exclamation-triangle" aria-hidden="true"></i> 주의</span>
              </span>
              <button
                v-if="riskPick"
                type="button"
                class="mad-risk__clear"
                @click="clearRiskPick"
              >필터 해제</button>
            </div>
            <div class="mad-risk__groups">
              <div v-for="group in growthRiskGroups" :key="'risk-' + group.areaKey" class="mad-risk__group">
                <div class="mad-risk__group-head">
                  <span class="mad-risk__group-title">{{ group.label }}</span>
                  <span v-if="group.period" class="mad-risk__group-period" title="판매장부 리스크는 매출현황에서 고른 기간으로 계산합니다">{{ group.period }}</span>
                  <span class="mad-risk__group-count" :title="'값을 받은 계정 ' + group.counted + '개 / 이 영역을 쓰는 계정 ' + group.total + '개 — 불러오는 중·조회실패·수집 전 계정은 세지 않습니다'">집계 {{ group.counted }}/{{ group.total }} 계정</span>
                </div>
                <div class="mad-risk__tiles">
                  <button
                    v-for="tile in group.tiles"
                    :key="tile.metric.key"
                    type="button"
                    class="mad-risk__tile"
                    :class="['mad-risk__tile--' + tile.tone, {
                      'mad-risk__tile--zero': !tile.accounts,
                      'mad-risk__tile--active': isRiskPicked(group.areaKey, tile.metric.key)
                    }]"
                    :disabled="!tile.accounts && !isRiskPicked(group.areaKey, tile.metric.key)"
                    :aria-pressed="isRiskPicked(group.areaKey, tile.metric.key) ? 'true' : 'false'"
                    :title="tile.accounts ? '누르면 아래 통합현황 표를 이 지표가 있는 계정만 남기고 큰 순서로 정렬합니다' : '해당 계정 없음'"
                    @click="toggleRiskPick(group.areaKey, tile.metric.key)"
                  >
                    <span class="mad-risk__tile-label">
                      <i
                        class="fas"
                        :class="tile.tone === 'warn' ? 'fa-exclamation-triangle' : 'fa-exclamation-circle'"
                        aria-hidden="true"
                      ></i>
                      <span class="sr-only">{{ tile.tone === 'warn' ? '주의' : '위험' }}</span>
                      {{ tile.metric.label }}
                    </span>
                    <span class="mad-risk__tile-main">{{ tile.accounts }}개 계정</span>
                    <span v-if="tile.metric.type === 'count' && tile.accounts" class="mad-risk__tile-sub">{{ tile.metric.tileUnit || '상품' }} {{ tile.sum.toLocaleString('ko-KR') }}개</span>
                    <span v-if="isRiskPicked(group.areaKey, tile.metric.key)" class="mad-risk__tile-picked">표 필터 중 · 다시 누르면 해제</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </b-card>
      </div>

      <!--
        계정별 수집 현황 — 이 화면의 첫 블록이다. 숫자를 읽기 전에 "그 숫자가 있기는 한가" 를 본다.
        ⚠ 칸마다 판정 기준이 다르다. 판매현황 둘은 **오늘 수집했는가**(원본 화면의 수집 버튼과 같은
          판정)고, 나머지는 **원본이 있는가**다. 서버가 basis 를 같이 주므로 머리글에 적는다 —
          안 적으면 같은 「수집완료」가 칸마다 다른 뜻인데 그걸 알 길이 없다.
        ⚠ 쓰지 않는 서비스는 「해당 없음」이다. 미수집으로 세면 할 일이 아닌 것이 할 일로 보인다.
        ⚠ 「수집완료」만으로는 **멈춘 수집이 안 보인다**(1건만 있어도 완료였다). 그래서 칸에
          날짜 — 그 데이터가 덮는 마지막 날 — 를 적고, 한참 밀린 칸은 초록으로 두지 않는다.
      -->
      <div class="col-lg-12" v-if="collectServices.length">
        <b-card class="mb-3 mad-block">
          <div class="mad-block__head">
            <h5 class="mad-block__title">계정별 수집 현황</h5>
            <span class="mad-block__note">날짜는 그 데이터가 있는 마지막 날입니다 — 쓰지 않는 서비스는 「해당 없음」으로 둡니다.</span>
          </div>
          <div class="multi-account-dashboard__table-wrap">
            <CDataTable
              storage-key="multiAccountDashboard.collect"
              :headers="collectHeaders"
              :items="collectRows"
              item-key="email"
              :fixedColumns="'계정'"
              :isPerPage="false"
              :isRadio="false"
              :isControls="false"
              :items-per-page="100"
              :height="'auto'"
            >
              <template v-slot:계정="{ item }">
                <div class="multi-account-dashboard__account">
                  <span class="multi-account-dashboard__account-dot" :style="accountDot(item.email)" aria-hidden="true"></span>
                  <span class="multi-account-dashboard__account-name" :title="item.계정">{{ item.계정 }}</span>
                  <span v-if="accountOf(item).isCurrent" class="badge badge-primary">현재</span>
                  <span v-else-if="accountOf(item).isOwner" class="badge badge-light border">대표</span>
                  <!-- 둘째 줄 = 이메일 + 어떤 서비스를 쓰는 계정인지. 이메일은 같은 이름이 겹칠 때
                       계정을 가르는 유일한 값이고, 서비스는 아래 「해당 없음」 칸들의 이유가 된다 -->
                  <span class="mad-collect__uses" :title="collectMetaOf(item)">{{ collectMetaOf(item) }}</span>
                </div>
              </template>

              <template v-for="svc in collectServices" v-slot:[svc.col]="{ item }">
                <span
                  :key="'collect-' + svc.col"
                  class="mad-chip"
                  :class="collectChipClass(item, svc)"
                  :title="collectTitle(svc, item[svc.col], collectAt(item, svc))"
                >{{ collectLabel(item[svc.col], collectAt(item, svc)) }}</span>
              </template>
            </CDataTable>
          </div>
          <div class="multi-account-dashboard__legend mad-collect__legend">
            <span class="multi-account-dashboard__legend-item">
              <span class="mad-chip mad-chip--collected">2026-09-15</span> 그 날까지 받아둔 상태
            </span>
            <span class="multi-account-dashboard__legend-item">
              <span class="mad-chip mad-chip--stale">2026-06-02</span> 한참 밀림 — 수집이 멈췄는지 확인
            </span>
            <span class="multi-account-dashboard__legend-item">
              <span class="mad-chip mad-chip--none">미수집</span> 쓰는 서비스인데 아직 안 받음
            </span>
            <span class="multi-account-dashboard__legend-item">
              <span class="mad-chip mad-chip--na">해당 없음</span> 그 계정이 쓰지 않는 서비스
            </span>
            <span class="multi-account-dashboard__legend-item">
              <span class="mad-chip mad-chip--error">확인불가</span> 조회에 실패해 단정하지 않음
            </span>
          </div>
        </b-card>
      </div>

      <!--
        매출현황(일자별) — 행이 일자다. 누르면 그 날의 계정별 값이 바로 아래에 붙는다.
        계정 비교는 위 통합현황이 맡고, 여기서 보려는 건 "요즘 어떤가" 라 날짜 축이 먼저다.
        ⚠ 표의 정렬을 껐다. 일자 행 바로 밑에 그 날의 계정 행이 붙어 있어서, 정렬하면
          부모와 자식이 흩어져 표가 거짓말을 한다.
      -->
      <template v-for="section in pageSections">
      <div
        class="col-lg-12"
        v-for="block in section.sales ? [section.sales] : []"
        :key="'sales-' + block.key"
        :id="blockAnchorId(block.key)"
      >
        <b-card class="mb-3 mad-block">
          <div class="mad-block__head">
            <h5 class="mad-block__title">{{ block.label }} · {{ salesRangeText(block) }}</h5>
            <span v-if="block.note" class="mad-block__note">{{ block.note }}</span>
            <span class="mad-block__spacer"></span>

            <span class="btn-group btn-group-sm mr-2" role="group" aria-label="조회 기간">
              <button
                v-for="preset in ['7d', '30d', '90d']"
                :key="block.key + '-' + preset"
                type="button"
                class="btn"
                :class="isSalesPresetActive(block, preset) ? 'btn-primary' : 'btn-outline-primary'"
                @click="applySalesPreset(block, preset)"
              >{{ presetLabel(preset) }}</button>
            </span>
            <input
              type="date"
              class="form-control form-control-sm multi-account-dashboard__period-date"
              :value="periodOf(block.areas[0]).start"
              @change="setSalesPeriod(block, { start: $event.target.value })"
            >
            <span class="mx-1">~</span>
            <input
              type="date"
              class="form-control form-control-sm multi-account-dashboard__period-date"
              :value="periodOf(block.areas[0]).end"
              @change="setSalesPeriod(block, { end: $event.target.value })"
            >
            <span v-if="salesPeriodInvalid(block)" class="multi-account-dashboard__period-warn">
              시작일이 종료일보다 뒤입니다 — 날짜를 고치면 다시 조회합니다
            </span>
            <button
              type="button"
              class="multi-account-dashboard__area-refresh"
              :disabled="!!switchingEmail"
              @click="refreshSales(block)"
            >이 영역만 새로고침</button>
          </div>

          <div v-if="block.loading" class="mad-sales__loading">
            <span class="multi-account-dashboard__spinner multi-account-dashboard__spinner--sm" aria-hidden="true"></span>
            불러오는 중…
          </div>
          <p v-else-if="!block.items.length" class="mb-0 text-muted">이 기간에는 값이 없습니다.</p>

          <div v-show="block.items.length" class="multi-account-dashboard__table-wrap">
            <CDataTable
              :storage-key="'multiAccountDashboard.' + block.key"
              :headers="block.headers"
              :items="block.items"
              item-key="_k"
              :fixedColumns="'일자'"
              :isPerPage="false"
              :items-per-page="1000"
              :height="'460px'"
              :rowClass="salesRowClass"
            >
              <template v-slot:일자="{ item }">
                <button
                  v-if="item.kind === 'date'"
                  type="button"
                  class="mad-sales__date"
                  :aria-expanded="isDateExpanded(block.key, item.date) ? 'true' : 'false'"
                  :title="'계정별로 ' + (isDateExpanded(block.key, item.date) ? '접기' : '펼치기')"
                  @click="toggleDate(block.key, item.date)"
                >
                  <span class="mad-sales__caret" aria-hidden="true">{{ isDateExpanded(block.key, item.date) ? '▾' : '▸' }}</span>
                  <span class="mad-sales__date-text">{{ item.date }}</span>
                  <!-- 계정이 하나뿐이면 「계정 1」은 알려주는 게 없다. 펼칠 값이 둘 이상일 때만 -->
                  <span v-if="item._children > 1" class="mad-sales__count">계정 {{ item._children }}</span>
                </button>
                <span v-else class="mad-sales__child">
                  <span class="multi-account-dashboard__account-dot" :style="accountDot(item.email)" aria-hidden="true"></span>
                  <span class="multi-account-dashboard__account-name" :title="item.계정">{{ item.계정 }}</span>
                </span>
              </template>

              <template v-for="col in block.columns" v-slot:[col.value]="{ item }">
                <span :key="col.value" class="multi-account-dashboard__cell">
                  <span
                    class="multi-account-dashboard__cell-value"
                    :class="salesClass(col, item)"
                  >{{ salesText(col, item) }}</span>
                </span>
              </template>
            </CDataTable>
          </div>

          <!--
            추이 차트 — 위 표와 **같은 데이터**로 그린다(따로 조회하면 그림과 숫자가 어긋난다).
            수량과 금액은 자릿수가 달라 축을 나눈다. 하루치만 있으면 선이 점 하나라 안 그린다.
          -->
          <div class="mad-chart" v-if="block.chartData">
            <div class="mad-chart__title">
              최근 추이
              <span class="mad-block__note">전체 계정 합산 · 왼쪽이 과거입니다</span>
            </div>
            <!--
              ⚠ 이 래퍼는 vue-apexcharts 가 아니다. type/options/series 가 아니라
                **DOM id(element) + 옵션과 series 를 합친 객체 하나(chart-option)** 를 받는다.
                (code-vue/src/components/charts/ApexChart.vue)
            -->
            <ApexChart
              :element="'mad-chart-' + block.key.replace('.', '-')"
              :chart-option="block.chartData"
            />
          </div>

          <!--
            날짜 축이 없는 값. 재고금액은 '지금 시점' 이고 매입가 미입력은 기간 전체의 건수라
            일자 행에 올리면 날 수만큼 겹쳐 세어진다 — 표 밖에 따로 적는다.
          -->
          <div class="mad-sales__aside" v-if="asideItems(block).length">
            <span
              v-for="aside in asideItems(block)"
              :key="aside.key"
              class="mad-sales__aside-item"
            >
              <span class="mad-sales__aside-label">{{ aside.label }}</span>
              <span
                v-for="value in aside.values"
                :key="aside.key + '-' + value.email"
                class="mad-sales__aside-value"
              >
                <span class="multi-account-dashboard__account-dot" :style="accountDot(value.email)" aria-hidden="true"></span>
                {{ value.name }} <b>{{ value.text }}</b>
              </span>
            </span>
          </div>
        </b-card>
      </div>

      <!--
        상품·공급 통합현황 — 상품관리와 공급관리(SCM)를 **한 표**로 합친다.
        따로 있을 때는 같은 계정을 보려고 두 표를 오르내려야 했다. 지표 키가 두 영역에서
        겹치므로(needPurchase·noInboundPrice …) 열 키를 「영역|지표」로 조합한다.
        열이 16~19개라 CDataTable 의 컨트롤 줄(표시할 컬럼·고정할 컬럼·내 설정 저장)을 켠다 —
        이 화면에서 가장 쓸모 있는 기능이고, 목업이 그리고 있는 드롭다운이 바로 그것이다.
        ⚠ 기본값은 **전체 표시**다(의뢰서 인수기준 2 — 엑셀의 모든 컬럼이 존재해야 한다).
      -->
      <div
        class="col-lg-12"
        v-for="block in section.combo ? [section.combo] : []"
        :key="'combo-' + block.key"
        :id="blockAnchorId(block.key)"
      >
        <b-card class="mb-3 mad-block">
          <div class="mad-block__head">
            <h5 class="mad-block__title">{{ block.label }}</h5>
            <span class="mad-block__note">{{ block.note }}</span>
            <span class="mad-block__spacer"></span>
            <!--
              기간을 쓰는 영역이 섞여 있으면(광고 성과) 날짜를 고를 수 있어야 한다.
              상품관리·공급관리는 '조회 시점의 상태' 라 기간이 없다 — 그때는 아예 안 그린다.
            -->
            <template v-if="comboPeriodArea(block)">
              <input
                type="date"
                class="form-control form-control-sm multi-account-dashboard__period-date"
                :value="periodOf(comboPeriodArea(block)).start"
                @change="setPeriod(comboPeriodArea(block), { start: $event.target.value })"
              >
              <span class="mx-1">~</span>
              <input
                type="date"
                class="form-control form-control-sm multi-account-dashboard__period-date"
                :value="periodOf(comboPeriodArea(block)).end"
                @change="setPeriod(comboPeriodArea(block), { end: $event.target.value })"
              >
            </template>
            <span
              v-if="comboBuiltAt(block)"
              class="multi-account-dashboard__built-at"
              title="이 표의 숫자를 마지막으로 집계한 시각입니다. 원본을 수집한 시각과는 다릅니다."
            >{{ comboBuiltAt(block) }}</span>
            <button
              type="button"
              class="multi-account-dashboard__area-refresh"
              :disabled="!!switchingEmail"
              @click="refreshCombo(block)"
            >이 영역만 새로고침</button>
          </div>
          <!-- 리스크 타일로 좁힌 상태. 무엇으로 좁혔는지와 푸는 길을 표 바로 위에 둔다 -->
          <div v-if="block.riskCol" class="mad-risk__filter-bar">
            <span>
              <b>{{ block.riskCol.metric.label }}</b> 이(가) 있는 계정만 · 큰 순서
              ({{ block.rows.length }}/{{ block.allRowCount }} 계정)
            </span>
            <button type="button" class="mad-risk__clear" @click="clearRiskPick">해제</button>
            <span v-if="!block.rows.length" class="mad-block__note">지금은 해당하는 계정이 없습니다 — 해제하면 전체가 다시 보입니다.</span>
          </div>

          <div class="multi-account-dashboard__table-wrap">
            <CDataTable
              :storage-key="'multiAccountDashboard.' + block.key"
              :headers="block.headers"
              :items="block.rows"
              item-key="email"
              :fixedColumns="'계정'"
              :isPerPage="false"
              :items-per-page="100"
              :height="'auto'"
            >
              <template v-slot:계정="{ item }">
                <div class="multi-account-dashboard__account">
                  <span class="multi-account-dashboard__account-dot" :style="accountDot(item.email)" aria-hidden="true"></span>
                  <span class="multi-account-dashboard__account-name" :title="item.계정">{{ item.계정 }}</span>
                  <span v-if="accountOf(item).isCurrent" class="badge badge-primary">현재</span>
                  <span v-else-if="accountOf(item).isOwner" class="badge badge-light border">대표</span>
                  <button
                    type="button"
                    class="multi-account-dashboard__account-go"
                    :disabled="!!switchingEmail"
                    :aria-label="item.계정 + ' 계정의 ' + block.parts[0].areaLabel + ' 화면으로 이동'"
                    :title="switchingEmail === item.email ? '전환 중…' : (block.parts[0].areaLabel + ' 화면으로 이동')"
                    @click="goToArea(areaOf(block.parts[0].area), accountOf(item))"
                  ><span aria-hidden="true">{{ switchingEmail === item.email ? '…' : '↗' }}</span></button>
                  <!-- 이름 아래 한 줄. 마크업 순서상 버튼 **뒤**에 둔다 —
                       앞에 두면 이 줄이 폭을 다 먹어 버튼이 셋째 줄로 밀린다 -->
                  <span class="multi-account-dashboard__account-email" :title="item.email">{{ item.email }}</span>
                </div>
              </template>

              <!--
                지표 셀 — 다섯 상태를 그대로 구분한다.
                해당 없음(그 계정이 안 쓰는 영역) / 수집 필요 / 조회 실패 / 값 없음 / 값(0 은 회색).
              -->
              <template v-for="col in block.columns" v-slot:[col.value]="{ item }">
                <span
                  :key="col.value"
                  class="multi-account-dashboard__cell"
                  :class="{ 'multi-account-dashboard__cell--na': isAreaNotApplicable(col.areaKey, accountOf(item)) }"
                >
                  <span
                    v-if="isAreaNotApplicable(col.areaKey, accountOf(item))"
                    class="multi-account-dashboard__cell-na"
                    title="이 계정은 해당 영역을 사용하지 않습니다"
                  >해당 없음</span>
                  <button
                    v-else-if="needOf(col.areaKey, accountOf(item))"
                    type="button"
                    class="multi-account-dashboard__cell-need multi-account-dashboard__cell-need--action"
                    :disabled="!!switchingEmail"
                    :aria-label="needLabelOf(col.areaKey, accountOf(item)) + ' — ' + col.areaLabel + ' 화면으로 이동'"
                    :title="'아직 수집하지 않았습니다. 누르면 ' + col.areaLabel + ' 화면으로 이동합니다.'"
                    @click="goToArea(areaOf(col.areaKey), accountOf(item))"
                  >
                    <span>{{ switchingEmail === item.email ? '전환 중…' : needLabelOf(col.areaKey, accountOf(item)) }}</span>
                    <span aria-hidden="true">›</span>
                  </button>
                  <span
                    v-else-if="cellOf(col.areaKey, item.email, col.metric.key).status === 'loading'"
                    class="multi-account-dashboard__cell-loading"
                    title="불러오는 중"
                    aria-label="불러오는 중"
                  ><i></i><i></i><i></i></span>
                  <span
                    v-else-if="cellOf(col.areaKey, item.email, col.metric.key).status === 'error'"
                    class="multi-account-dashboard__cell-error"
                    title="조회에 실패했습니다"
                  >조회실패</span>
                  <span
                    v-else-if="cellOf(col.areaKey, item.email, col.metric.key).status === 'empty'"
                    class="multi-account-dashboard__cell-empty"
                    title="값이 없습니다"
                  >—</span>
                  <button
                    v-else-if="col.metric.link"
                    type="button"
                    class="multi-account-dashboard__cell-value multi-account-dashboard__cell-link"
                    :class="valueClass(cellOf(col.areaKey, item.email, col.metric.key), col.metric)"
                    :disabled="!!switchingEmail"
                    :title="'누르면 ' + item.계정 + ' 계정의 ' + col.areaLabel + ' 화면에서 해당 상품만 봅니다'"
                    @click="goToArea(areaOf(col.areaKey), accountOf(item), col.metric.link.query)"
                  >{{ formatCell(cellOf(col.areaKey, item.email, col.metric.key), col.metric) }}</button>
                  <span
                    v-else
                    class="multi-account-dashboard__cell-value"
                    :class="valueClass(cellOf(col.areaKey, item.email, col.metric.key), col.metric)"
                    :title="cellOf(col.areaKey, item.email, col.metric.key).value === 0 ? '정상(0건)' : ''"
                  >{{ formatCell(cellOf(col.areaKey, item.email, col.metric.key), col.metric) }}</span>
                  <span
                    v-if="cellNote(cellOf(col.areaKey, item.email, col.metric.key))"
                    class="multi-account-dashboard__cell-sub"
                    :title="cellNote(cellOf(col.areaKey, item.email, col.metric.key)).title"
                  >{{ cellNote(cellOf(col.areaKey, item.email, col.metric.key)).text }}</span>
                </span>
              </template>
            </CDataTable>
          </div>
        </b-card>
      </div>
      </template>


    </div>
  </div>
</template>

<script>
import axios from 'axios';
import CDataTable from '@/components/common/CDataTable';
// 판매장부 화면(growthSales.vue)이 쓰는 래퍼를 그대로 쓴다 — 차트 라이브러리를 새로 들이지 않는다
import ApexChart from '@/components/charts/ApexChart';
import AssetChangeTiles from '@/components/growth/AssetChangeTiles';

/**
 * 영역 정의 — 화면에 그리는 순서 그대로다.
 * label / route 는 서버(POST /api/dashboard/accounts 의 areas)가 주면 서버 값이 이긴다.
 * 여기 값은 서버가 아직 그 영역을 안 내려줄 때 쓰는 기본값이다.
 *
 * metric.type : count(건) | money(원) | percent(%) | bool(여부)
 * metric.tone : 'alert' 면 0 이 아닐 때 빨강(=쌓이면 안 되는 값), 'warn' 이면 노랑(=지켜볼 값)
 */
const AREA_DEFS = [
  {
    key: 'growth.productdb',
    label: '로켓그로스 > 상품관리',
    route: 'growth.growthdb',
    metrics: [
      { key: 'exposureIdChanged', label: '노출ID변경', type: 'count', tone: 'alert' },
      { key: 'rivalMatched', label: '타사매칭', type: 'count', tone: 'alert' },
      { key: 'itemLoser', label: '아이템루저', type: 'count', tone: 'alert' },
      { key: 'saleSuspended', label: '판매중지', type: 'count', tone: 'alert' },
      { key: 'soldOut', label: '품절', type: 'count', tone: 'alert' },
      { key: 'noBadge', label: '뱃지없음', type: 'count', tone: 'alert' },
      { key: 'returned', label: '반품', type: 'count', tone: 'alert' },
      { key: 'bundle', label: '번들', type: 'count' },
      { key: 'noPurchaseInfo', label: '구매정보없음', type: 'count', tone: 'alert' },
      { key: 'noInboundPrice', label: '매입정보없음', type: 'count', tone: 'alert' }
    ]
  },
  {
    key: 'growth.scm',
    label: '로켓그로스 > 공급관리(SCM)',
    route: 'growth.growthscmV0',
    metrics: [
      { key: 'salesCollected', label: '판매현황 수집여부', type: 'bool', trueLabel: '수집됨', falseLabel: '미수집' },
      { key: 'needPurchase', label: '구매필요', type: 'count', tone: 'alert' },
      { key: 'needForwarderOut', label: '배대지출고필요', type: 'count', tone: 'alert' },
      { key: 'needDomesticOut', label: '국내출고필요', type: 'count', tone: 'alert' },
      { key: 'soldOutWarning', label: '품절예고', type: 'count', tone: 'alert' },
      { key: 'overStockRisk', label: '과재고위험', type: 'count', tone: 'alert' },
      { key: 'monthlySalesAbnormal', label: '월판매이상', type: 'count', tone: 'alert' },
      { key: 'returnStock', label: '반품재고', type: 'count' },
      { key: 'hiddenProduct', label: '숨김상품', type: 'count' },
      // 90일 넘게 확인 안 된 재고(개). 보조줄에 원가 금액. 누르면 그 계정 SCM 의 미확인 목록으로 간다.
      // 계산은 SCM 화면과 같은 서버 함수(growth-recon) — 대시보드가 따로 세지 않는다
      // access: 'recon' — 보는 사람(로그인한 본인)이 재고 대사를 볼 수 있을 때만. 판단은 서버(accounts.canSeeRecon)
      { key: 'reconRequest', label: '확인요청 대상', type: 'count', tone: 'alert', tileUnit: '재고',
        link: { query: { recon: 'over90' } }, access: 'recon' }
    ]
  },
  {
    key: 'growth.ledger',
    label: '로켓그로스 > 판매장부',
    route: 'growth.salesledger',
    // 의뢰서 §4: "시작일·종료일, 최근 7/30/90일, 월별조회. 같은 영역의 모든 계정에 같은 조건"
    //
    // ⚠ note 가 필요한 이유 — 「매입가 미입력여부 공지사항」의 기준이 원본 화면과 다르다.
    //   여기(대시보드)는 **고른 기간**의 매입가 미입력 건수로 판정한다(excludedRows).
    //   판매장부 화면의 같은 공지는 요약장부에서 오는 **연 단위**다
    //   (growthSalesLedger.vue:2062 loadMonthlySummary(year) → missingPurchasePriceCount).
    //   그래서 연초~오늘에서는 둘이 같고, 기간을 좁히면 갈린다. 둘 다 자기 기준으로는 맞다.
    //   지표 라벨은 의뢰서 엑셀 문구 그대로여야 해서(인수기준 2) 라벨에 기준을 못 붙인다 ⇒ 여기 적는다.
    period: {
      range: true, presets: ['7d', '30d', '90d'], months: true,
      note: '매입가 미입력 공지도 고른 기간 기준입니다 (판매장부 화면의 같은 공지는 연 단위)'
    },
    metrics: [
      { key: 'inboundPriceNotice', label: '매입가 미입력여부 공지사항', type: 'bool', trueLabel: '있음', falseLabel: '없음', tone: 'alert' },
      { key: 'grossSales', label: '총매출액', type: 'money', emphasis: 'strong' },
      { key: 'netSales', label: '순매출액(쿠폰제외)', type: 'money' },
      { key: 'grossProfit', label: '총이익(GP)', type: 'money', emphasis: 'strong' },
      { key: 'vat', label: '부가세', type: 'money', emphasis: 'muted' },
      { key: 'totalPurchase', label: '총매입액', type: 'money' },
      { key: 'totalAdCost', label: '총광고비', type: 'money' },
      { key: 'marginRate', label: '총마진율', type: 'percent' },
      { key: 'roas', label: 'ROAS', type: 'percent' },
      { key: 'roi', label: 'ROI', type: 'percent' },
      { key: 'stockAmount', label: '재고금액', type: 'money' },
      // 리스크 필터 개수(상품 수) — 서버 ledgerRiskFlags 판정. ROI/ROAS 하락은 14일 미만이면 '-'
      { key: 'lossByAd', label: '광고로 적자', type: 'count', tone: 'alert' },
      { key: 'lossByReturn', label: '반품으로 적자', type: 'count', tone: 'alert' },
      { key: 'lossByStorage', label: '보관비로 적자', type: 'count', tone: 'alert' },
      { key: 'lossStructural', label: '팔수록 적자', type: 'count', tone: 'alert' },
      { key: 'storageOnly', label: '안 팔리고 보관비만', type: 'count', tone: 'alert' },
      { key: 'noPurchasePrice', label: '매입가 없음', type: 'count', tone: 'alert' },
      { key: 'adLossAfterReturn', label: '반품 반영 시 광고적자', type: 'count', tone: 'warn' },
      { key: 'roiDecline', label: 'ROI 하락', type: 'count', tone: 'warn' },
      { key: 'roasDecline', label: 'ROAS 하락', type: 'count', tone: 'warn' }
    ]
  },
  {
    key: 'rocket.productdb',
    label: '로켓배송 > 상품관리(신규)',
    route: 'product.rocketProductManagement',
    metrics: [
      { key: 'exposureIdChanged', label: '노출ID변경', type: 'count', tone: 'alert' },
      { key: 'supplyPriceChanged', label: '공급가변경', type: 'count', tone: 'alert' },
      { key: 'rivalMatched', label: '타사매칭', type: 'count', tone: 'alert' },
      { key: 'itemLoser', label: '아이템루저', type: 'count', tone: 'alert' },
      { key: 'orderBlocked', label: '발주불가', type: 'count', tone: 'alert' },
      { key: 'noPurchaseInfo', label: '구매정보없음', type: 'count', tone: 'alert' },
      { key: 'noInboundPrice', label: '매입정보없음', type: 'count', tone: 'alert' }
    ]
  },
  {
    key: 'rocket.scm',
    label: '로켓배송 > 공급관리(신규)',
    route: 'product.rocketScm',
    metrics: [
      { key: 'salesCollected', label: '판매현황 수집여부', type: 'bool', trueLabel: '수집됨', falseLabel: '미수집' },
      { key: 'needPurchase', label: '구매필요', type: 'count', tone: 'alert' },
      { key: 'needForwarderOut', label: '배대지출고필요', type: 'count', tone: 'alert' },
      { key: 'needDomesticOut', label: '국내출고필요', type: 'count', tone: 'alert' },
      { key: 'soldOutWarning', label: '품절예고', type: 'count', tone: 'alert' },
      { key: 'overStockRisk', label: '과재고위험', type: 'count', tone: 'alert' },
      { key: 'monthlySalesAbnormal', label: '월판매이상', type: 'count', tone: 'alert' },
      { key: 'returnStock', label: '반품재고', type: 'count' },
      { key: 'hiddenProduct', label: '숨김상품', type: 'count' }
    ]
  },
  {
    key: 'rocket.ledger',
    label: '로켓배송 > 판매장부(신규)',
    route: 'product.rocketSalesLedger',
    // 의뢰서 §4: "시작일·종료일을 지정하고 동일 조건으로 계정별 비교" — 프리셋은 요구에 없다
    period: { range: true },
    metrics: [
      { key: 'supplySales', label: '총공급매출', type: 'money', emphasis: 'strong' },
      { key: 'grossProfit', label: '총마진(GP)', type: 'money', emphasis: 'strong' },
      { key: 'totalPurchase', label: '총매입액', type: 'money' },
      { key: 'totalAdCost', label: '총광고비', type: 'money' },
      { key: 'marginRate', label: '총마진율', type: 'percent' },
      { key: 'roas', label: 'ROAS', type: 'percent' },
      { key: 'roi', label: 'ROI', type: 'percent' }
    ]
  },
  {
    key: 'rocket.polist',
    label: '로켓배송 > 발주리스트',
    route: 'category.listcategory',
    // 의뢰서 §4: "오늘/7일/1개월, 발주일 또는 입고예정일, 시작일·종료일. '최근 1개월'로 고정하지 않는다"
    period: { range: true, presets: ['today', '7d', '1m'], basis: ['발주일시', '입고예정일'] },
    metrics: [
      { key: 'poAmount', label: '총발주금액', type: 'money', emphasis: 'strong' },
      { key: 'poCanceledAmount', label: '발주취소금액', type: 'money' },
      { key: 'poSales', label: '발주매출', type: 'money', emphasis: 'strong' },
      { key: 'poProfit', label: '발주(수익)', type: 'money' },
      { key: 'confirmedProfit', label: '확정(수익)', type: 'money' },
      { key: 'inboundIncome', label: '입고(수입)', type: 'money' },
      { key: 'noInboundPrice', label: '매입가 미입력', type: 'count', tone: 'alert' }
    ]
  },
  {
    key: 'ad.verify',
    label: '광고 성과',
    // 사이드바 「광고관리(상품검증)」의 실제 라우트. growth.admanager 는 옆 메뉴(광고관리)라 다른 화면이다
    route: 'Ad.adSourcing',
    /*
     * ⚠ 이 영역만 지표의 기간 기준이 두 가지다.
     *   앞 5개(노출수·클릭수·광고비·CTR·CPC) = 일별 광고데이터라 위에서 고른 **기간**을 따른다.
     *   뒤 4개(광고이익·광고ROI·판매지속가능/불가) = 광고분석을 돌린 **그 시점의 스냅샷**이라
     *     기간을 바꿔도 값이 안 변한다.
     * 두 광고비는 집계 기준이 달라 서로 다른 값이다. 화면에서 더하거나 비교하면 안 되므로
     * 뒤 4개 라벨에 '(분석시점)' 을 붙여 눈으로 구분되게 둔다.
     *
     * 맨 뒤 3개(캠페인수·운영중·총예산)는 기준이 또 하나 다르다. 캠페인은 날짜별 스냅샷이라
     * 기간으로 더하면 같은 캠페인이 날짜 수만큼 겹쳐 세어진다(실측 최대 89배). 그래서 기간과
     * 무관하게 **가장 최근 스냅샷**만 본다 — 라벨에 '(현재)' 를 붙여 그걸 표시한다.
     *
     * 마지막 '분석기간' 은 숫자 지표가 아니라 **기준 표기**다(의뢰서 §4 "원본 분석 결과의 실제
     * 기간을 계정별 표시"). 계정마다 분석을 돌린 기간이 달라서, 옆 계정과 같은 기준인지
     * 눈으로 확인할 자리가 필요하다. 2026-09-10 이전 분석분에는 이 값이 없어 '값 없음'으로 뜬다.
     */
    // 앞 5개만 기간을 따른다. 시작·종료일만 두는 이유는 의뢰서 §4 가 광고에 프리셋을 요구하지
    // 않았고, "공통 기간 변경 가능 여부는 원본 기능 확인 후 결정" 이라고만 적었기 때문이다.
    period: { range: true },
    metrics: [
      { key: 'impressions', label: '노출수', type: 'count' },
      { key: 'clicks', label: '클릭수', type: 'count' },
      // ⚠ 라벨에 '(기간)' 을 반드시 붙인다. 광고관리(상품검증) 화면에도 「광고비」가 있는데
      //   그건 분석시점 누적(AdSourcingList 3분할 합)이라 **다른 값**이다. 표기가 없으면
      //   두 화면을 나란히 놓고 "왜 다르냐" 가 된다 — 실측 Leblanc 41,016,933 vs 27,982,715.
      { key: 'adCost', label: '광고비(기간)', type: 'money', emphasis: 'strong' },
      { key: 'ctr', label: 'CTR', type: 'percent' },
      { key: 'cpc', label: 'CPC', type: 'money' },
      { key: 'adProfit', label: '광고이익(분석시점)', type: 'money' , snapshot: true },
      { key: 'adRoi', label: '광고ROI(분석시점)', type: 'percent' , snapshot: true },
      { key: 'continuable', label: '판매지속가능(분석시점)', type: 'count' , snapshot: true },
      { key: 'notContinuable', label: '판매지속불가(분석시점)', type: 'count', tone: 'alert' , snapshot: true },
      { key: 'campaignCount', label: '캠페인수(현재)', type: 'count' , snapshot: true },
      { key: 'campaignActive', label: '운영중(현재)', type: 'count' , snapshot: true },
      { key: 'campaignBudget', label: '총예산(현재)', type: 'money' , snapshot: true },
      { key: 'analysisPeriod', label: '분석기간(분석시점)', type: 'text' }
    ]
  }
];

/**
 * 매출현황(일자별) 블록 — 행이 **일자**고, 펼치면 그 날의 계정별 값이 아래에 붙는다.
 *
 * 왜 일자가 행인가: 계정 비교는 위 통합현황이 한다. 여기서 보려는 건 "요즘 어떤가" 라
 * 날짜 축이 먼저다. 계정별 값은 궁금할 때만 펼친다.
 *
 * ⚠ 열은 **더할 수 있는 값**과 그 재료만 둔다. 비율은 날마다 분모가 달라 더할 수 없어서
 *   derive 로 그 날 값에서 계산한다(합계 행도 같은 식이라 표 안에서 어긋나지 않는다).
 * ⚠ 재고금액처럼 '지금 시점' 인 값은 날짜 축이 없다 — 표 아래 aside 줄로 뺀다.
 */
const SALES_BLOCKS = [
  {
    key: 'growth.sales',
    label: '로켓그로스 매출현황',
    areas: ['growth.ledger'],
    // 표 아래에 따로 적는 값(날짜 축이 없는 것들)
    aside: [{ area: 'growth.ledger', keys: ['stockAmount', 'inboundPriceNotice'] }],
    columns: [
      { value: 'saleQuantity', label: '총판매량', type: 'count', from: 'growth.ledger' },
      { value: 'saleAmount', label: '총매출액', type: 'money', from: 'growth.ledger', emphasis: 'strong' },
      { value: 'netSales', label: '순매출액(쿠폰제외)', type: 'money', from: 'growth.ledger' },
      { value: 'adTotal', label: '총광고비', type: 'money', from: 'growth.ledger' },
      { value: 'adNetProfit', label: '광고순수익', type: 'money', from: 'growth.ledger' },
      { value: 'grossProfit', label: '총이익(GP)', type: 'money', from: 'growth.ledger', emphasis: 'strong' },
      { value: 'cogs', label: '총매입액', type: 'money', from: 'growth.ledger' },
      { value: 'vat', label: '부가세', type: 'money', from: 'growth.ledger', emphasis: 'muted' },
      { value: 'marginRate', label: '총마진율', type: 'percent', from: 'growth.ledger',
        derive: (r) => (r.saleAmount > 0 ? Math.round((r.grossProfit / r.saleAmount) * 100) : 0) },
      { value: 'roas', label: 'ROAS', type: 'percent', from: 'growth.ledger',
        derive: (r) => (r.adTotal > 0 ? Math.round((r.adRevenue / r.adTotal) * 100) : 0) },
      { value: 'roi', label: 'ROI', type: 'percent', from: 'growth.ledger',
        derive: (r) => (r.cogs > 0 ? Math.round((r.grossProfit / (r.cogs * 10 / 11)) * 100) : 0) },
      { value: 'excludedRows', label: '매입가 미입력', type: 'count', from: 'growth.ledger', tone: 'alert' }
    ],
    // 추이 차트에 그릴 계열. 열 전부를 그리면 선이 겹쳐 아무것도 안 읽힌다 — 네 개만 고른다.
    // 수량과 금액은 자릿수가 달라 축을 나눈다(한 축에 두면 판매량이 바닥에 붙어 사라진다)
    chart: [
      { value: 'saleQuantity', label: '총판매량', type: 'column', axis: 'qty' },
      { value: 'netSales', label: '순매출', type: 'line', axis: 'money' },
      { value: 'adTotal', label: '광고비', type: 'line', axis: 'money' },
      { value: 'grossProfit', label: '총이익', type: 'line', axis: 'money' }
    ]
  },
  {
    key: 'rocket.sales',
    label: '로켓배송 판매현황',
    areas: ['rocket.polist', 'rocket.ledger'],
    // ⚠ 한 표에 기준이 **둘**이다. 발주·입고는 발주일, 판매는 판매일이다.
    //   의뢰서 §7 이 "교차 합산하지 않는다" 고 못박은 지점이라 **더하지 않고 나란히**만 둔다.
    //   머리글에 기준을 적어 같은 줄의 숫자가 같은 날의 것이 아님을 보이게 한다.
    note: '발주·입고는 발주일 기준, 판매는 판매일 기준입니다 — 서로 더하지 마세요.',
    aside: [{ area: 'rocket.polist', keys: ['noInboundPrice'] }],
    columns: [
      { value: 'poAmount', label: '총발주금액', type: 'money', from: 'rocket.polist', basis: '발주일', emphasis: 'strong' },
      { value: 'inboundAmount', label: '입고금액', type: 'money', from: 'rocket.polist', basis: '발주일' },
      { value: 'poCanceledAmount', label: '발주취소금액', type: 'money', from: 'rocket.polist', basis: '발주일' },
      { value: 'poSales', label: '발주매출', type: 'money', from: 'rocket.polist', basis: '발주일',
        derive: (r) => (r.poAmount || 0) - (r.poCanceledAmount || 0) },
      { value: 'poProfit', label: '발주(수익)', type: 'money', from: 'rocket.polist', basis: '발주일' },
      { value: 'confirmedProfit', label: '확정(수익)', type: 'money', from: 'rocket.polist', basis: '발주일' },
      { value: 'inboundIncome', label: '입고(수익)', type: 'money', from: 'rocket.polist', basis: '발주일' },
      { value: 'saleQuantity', label: '판매량', type: 'count', from: 'rocket.ledger', basis: '판매일' },
      { value: 'supplySales', label: '총공급매출', type: 'money', from: 'rocket.ledger', basis: '판매일', emphasis: 'strong' },
      { value: 'grossProfit', label: '총마진(GP)', type: 'money', from: 'rocket.ledger', basis: '판매일', emphasis: 'strong' },
      { value: 'totalPurchase', label: '총매입액', type: 'money', from: 'rocket.ledger', basis: '판매일' },
      { value: 'adTotal', label: '총광고비', type: 'money', from: 'rocket.ledger', basis: '판매일' },
      { value: 'netProfit', label: '순수익', type: 'money', from: 'rocket.ledger', basis: '판매일' },
      { value: 'marginRate', label: '총마진율', type: 'percent', from: 'rocket.ledger', basis: '판매일',
        derive: (r) => (r.supplySales > 0 ? Math.round((r.grossProfit / r.supplySales) * 100) : 0) },
      { value: 'roas', label: 'ROAS', type: 'percent', from: 'rocket.ledger', basis: '판매일',
        derive: (r) => (r.adTotal > 0 ? Math.round((r.adRevenue / r.adTotal) * 100) : 0) },
      { value: 'roi', label: 'ROI', type: 'percent', from: 'rocket.ledger', basis: '판매일',
        derive: (r) => (r.totalPurchase > 0 ? Math.round((r.grossProfit / (r.totalPurchase * 10 / 11)) * 100) : 0) }
    ],
    chart: [
      { value: 'saleQuantity', label: '판매량', type: 'column', axis: 'qty' },
      { value: 'poAmount', label: '총발주금액', type: 'line', axis: 'money' },
      { value: 'supplySales', label: '공급매출', type: 'line', axis: 'money' },
      { value: 'adTotal', label: '광고비', type: 'line', axis: 'money' },
      { value: 'netProfit', label: '순수익', type: 'line', axis: 'money' }
    ]
  }
];

// 추이 차트 색. 계정 점 색과 섞이지 않게 따로 둔다(그쪽은 계정, 이쪽은 지표다)
const CHART_COLORS = ['#a8c6ea', '#2f6bbd', '#e08b3a', '#1d7a45', '#7b52c4'];

// 일자별 표가 한 화면에 보여줄 기본 기간. 목업이 「최근 30일」이고, 그 이상은 스크롤만 길어진다
const SALES_DEFAULT_DAYS = 30;

/**
 * 상품·공급 통합현황 — 상품관리 + 공급관리(SCM)를 한 표로 합친 블록.
 * parts 는 AREA_DEFS 의 key 만 가리킨다(라벨·지표·단위는 거기서 가져온다).
 * group/groupClass 는 표 머리글에 붙는 묶음 뱃지다 — 한 표 안에서 어느 화면 지표인지 가른다.
 */
const COMBO_BLOCKS = [
  {
    key: 'growth.combo',
    label: '로켓그로스 > 상품·공급 통합현황',
    note: '상품관리와 공급관리(SCM) 지표를 한 표에서 선택해 확인합니다.',
    parts: [
      { area: 'growth.productdb', group: '상품관리', groupClass: 'is-blue' },
      { area: 'growth.scm', group: '공급관리(SCM)', groupClass: 'is-green' },
      // 판매장부는 리스크 9개만 싣는다(금액 지표는 매출현황이 책임진다).
      // ⚠ 이 값만 **기간 기준**이다 — 매출현황에서 고른 판매장부 기간. 날짜 칸을 여기 또 두면
      //   일자 표와 기간이 갈리므로 입력은 두지 않고 묶음 뱃지에 기간을 적는다(periodInGroup).
      {
        area: 'growth.ledger', group: '판매장부', groupClass: '', periodInGroup: true,
        keys: ['lossByAd', 'lossByReturn', 'lossByStorage', 'lossStructural', 'storageOnly',
          'noPurchasePrice', 'adLossAfterReturn', 'roiDecline', 'roasDecline']
      }
    ]
  },
  {
    key: 'rocket.combo',
    label: '로켓배송 > 상품·공급 통합현황',
    note: '상품관리와 공급관리 지표를 한 표에서 선택해 확인합니다.',
    parts: [
      { area: 'rocket.productdb', group: '상품관리', groupClass: 'is-blue' },
      { area: 'rocket.scm', group: '공급관리', groupClass: 'is-green' }
    ]
  },
  // 광고 성과는 영역이 하나뿐이라 '합칠 것' 이 없지만, 계정 × 지표라는 틀이 같다.
  // 같은 블록 정의를 쓰면 마크업을 새로 만들지 않아도 되고 열 선택·고정도 그대로 붙는다.
  // ⚠ 이 영역만 기간 기준이 셋이다(기간 / 분석시점 / 현재). 라벨에 이미 적혀 있으므로
  //   블록 설명에서 한 번 더 짚어 준다 — 섞어서 읽으면 안 되는 숫자들이다.
  {
    key: 'ad.block',
    label: '광고 성과',
    note: '노출·클릭·광고비는 고른 기간, (분석시점)은 광고분석을 돌린 때, (현재)는 지금 값입니다.',
    parts: [
      { area: 'ad.verify', group: '광고', groupClass: 'is-blue' }
    ]
  }
];

/**
 * 합친 표의 열 키 = 「m_영역키__지표키」.
 * ① 지표 키가 두 영역에서 겹친다(needPurchase·noInboundPrice·hiddenProduct …) — 영역을 앞에 붙인다.
 * ② 점을 쓰지 않는다. Vuetify 는 header.value 를 **경로**로 읽어서 'growth.scm|x' 를
 *    item.growth 밑에서 찾는다(값이 안 잡히고 정렬·필터가 조용히 어긋난다).
 */
const comboColumnValue = (areaKey, metricKey) =>
  'm_' + areaKey.replace(/[.]/g, '_') + '__' + metricKey;

/* ────────────────────────────────────────────────────────────────
 * 지표 값 = 아직 가상값(mock)
 *
 * 실제 지표 API 가 생기면 아래 두 줄만 바꾸면 된다.
 *   METRIC_API_READY = true  로 켜고, METRIC_API_URL 을 실제 경로로.
 * fetchAreaMetrics() 가 mock 대신 서버를 부르며, 서버 응답 형태는
 * buildMockAreaMetrics() 가 돌려주는 것과 같은 모양이면 된다:
 *   { 'a@b.com': { metricKey: { status: 'ok'|'empty'|'error', value: Number|Boolean } } }
 * 화면 코드(셀 렌더링/포맷/상태표시)는 손대지 않아도 된다.
 * ──────────────────────────────────────────────────────────────── */
const METRIC_API_READY = true;
const METRIC_API_URL = '/api/dashboard/metrics';

// 계정·영역·지표가 같으면 늘 같은 값이 나오게 하는 결정적 해시(가상값 전용).
// 새로고침할 때마다 숫자가 춤추면 화면 합의를 못 한다.
const mockHash = (seed) => {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
};

const buildMockCell = (areaKey, email, metric) => {
  const h = mockHash(areaKey + '|' + email + '|' + metric.key);

  // 세 가지 상태(정상 0 / 값 없음 / 조회 실패)가 화면에 모두 보이도록 일부러 섞는다
  if (h % 23 === 0) return { status: 'error', value: null };
  if (h % 17 === 0) return { status: 'empty', value: null };

  if (metric.type === 'bool') return { status: 'ok', value: h % 4 !== 0 };
  if (metric.type === 'percent') return { status: 'ok', value: Math.round((h % 4500) / 10) / 10 };
  if (metric.type === 'money') return { status: 'ok', value: (h % 90000) * 1000 };
  if (h % 5 === 0) return { status: 'ok', value: 0 }; // 정상 0 을 일부러 만든다
  return { status: 'ok', value: h % 320 };
};

const buildMockAreaMetrics = (areaKey, accounts) => {
  const area = AREA_DEFS.find((item) => item.key === areaKey);
  const result = {};
  if (!area) return result;
  accounts.forEach((account) => {
    const cells = {};
    area.metrics.forEach((metric) => {
      cells[metric.key] = buildMockCell(areaKey, account.email, metric);
    });
    result[account.email] = cells;
  });
  return result;
};

const EMPTY_CELL = { status: 'empty', value: null };

// 「지금 볼 것」에 한 번에 보일 칩 수. 더 많으면 그 자체가 잡음이라 나머지는 숫자로 알린다
const ATTENTION_CHIP_LIMIT = 12;
// 계정 구분용 점 색. 순서대로 돌려 쓴다 — 이웃한 계정끼리 최대한 벌어지게 골랐고
// 채도·명도를 맞춰 어느 하나가 유난히 튀거나 묻히지 않는다.
const ACCOUNT_DOT_COLORS = [
  '#3572b0', '#d9534f', '#2e9e6b', '#b8860b', '#7b52c4',
  '#0f9aa8', '#c25a9b', '#6b7a8f'
];
export default {
  name: 'MultiAccountDashboard',
  components: { CDataTable, ApexChart, AssetChangeTiles },
  data() {
    return {
      loading: false,
      loadError: '',
      accounts: [],
      serverAreas: [],
      viewerCanSeeRecon: false,   // 보는 사람(로그인한 본인)이 재고 대사 지표를 볼 수 있나 — 서버 accounts.canSeeRecon
      // 'areaKey|email' -> { metricKey: { status, value } }
      metricCells: {},
      // 'areaKey' -> 'idle' | 'loading'
      areaStatus: {},
      // 영역별 '이 숫자가 만들어진 시각'(ISO). 서버 builtAt 중 가장 오래된 것.
      // 의뢰서 §4 가 가른 셋 중 **마지막 조회(집계) 시각**이다 — 원본 수집 시각이 아니다.
      areaBuiltAt: {},
      switchingEmail: '',
      // 계정별 수집 현황. services 는 서버가 순서까지 정해 준다(화면이 목록을 또 갖지 않는다).
      // collect[email][서비스키] = 'collected' | 'none' | 'na' | 'error'
      collectServices: [],
      collect: {},
      // 같은 칸의 '마지막 날짜'. collect 와 따로 두는 이유는 서버 응답이 그렇게 나뉘어서다 —
      // 상태 문자열(collect)은 예전 형태 그대로 두고 날짜만 덧붙였다.
      // collectLast[email][서비스키] = { date: 'YYYY-MM-DD'|null, stale: true|false }
      collectLast: {},
      // 일자별 값. dailyRows[areaKey][email] = [ { date, ... } ] — null 이면 그 계정 조회 실패
      dailyRows: {},
      // 'areaKey' -> 'idle' | 'loading'
      dailyStatus: {},
      // 펼친 일자. expandedDates[blockKey][date] = true
      expandedDates: {},
      // 영역별 조회 기간. **하나로 묶지 않는다** — 의뢰서 §4 가 영역마다 다른 조건을 요구한다
      // (그로스장부는 7/30/90일·월별, 로켓장부는 시작·종료일, 발주리스트는 기준일 전환까지).
      // 서버는 영역마다 따로 요청받으므로 영역별 기간이 그대로 전달된다.
      // 한 영역 안에서는 모든 계정이 같은 조건이다(§4 "같은 영역의 모든 계정에 같은 조건").
      // 'areaKey' -> { start: 'YYYY-MM-DD', end: 'YYYY-MM-DD', basis: '발주일시'|'입고예정일' }
      areaPeriod: {},
      // 리스크 타일로 좁힌 지표. null 이면 통합현황 표는 원래대로. { areaKey, metricKey }
      riskPick: null
    };
  },
  computed: {
    // 판매장부 라우트 가드(requiresAuth: showgrowth|showSCM)와 같은 조건
    canSeeAssetTiles() {
      const auth = (this.$store.state.user && this.$store.state.user.auth) || {};
      return !!(auth.showgrowth || auth.showSCM);
    },
    /**
     * 서버가 준 areas 로 label/route 를 덮어쓴다. 순서는 AREA_DEFS 를 따른다 —
     * 모든 표가 같은 순서로 보여야 계정끼리 눈으로 비교가 된다.
     */
    mergedAreas() {
      return AREA_DEFS.map((def) => {
        const fromServer = this.serverAreas.find((item) => item.key === def.key);
        return {
          key: def.key,
          label: (fromServer && fromServer.label) || def.label,
          route: (fromServer && fromServer.route) || def.route,
          metrics: def.metrics.filter((m) => m.access !== 'recon' || this.viewerCanSeeRecon)
        };
      });
    },
    /**
     * 수집 현황 표의 열. 서비스 목록·순서는 서버(DASHBOARD_COLLECT)가 정하고 화면은 그리기만 한다 —
     * 목록을 양쪽에 두면 서비스가 늘 때 한쪽만 고쳐 조용히 어긋난다.
     * ⚠ 열 키에 점을 쓰지 않는다. Vuetify 는 header.value 를 **경로**로 읽어서
     *   'growth.productdb' 를 item.growth.productdb 로 찾아 들어간다(값이 안 잡힌다).
     */
    collectHeaders() {
      const headers = [{ text: '계정', value: '계정', isSlot: true, align: 'start', width: '280px' }];
      this.collectServices.forEach((svc) => {
        headers.push({
          // 기준을 머리글에 적는다 — '오늘' 기준인 칸과 '있기만 하면' 인 칸이 섞여 있다
          text: this.headerText(svc.label) + (svc.basis === 'today' ? '\n(오늘 기준)' : ''),
          value: svc.col, isSlot: true, align: 'center'
        });
      });
      return headers;
    },
    collectRows() {
      return this.accounts.map((account) => {
        const row = { email: account.email, 계정: this.accountTitle(account) };
        const got = this.collect[account.email] || {};
        this.collectServices.forEach((svc) => {
          // 아직 안 받았으면 빈칸으로 둔다. 모르는 것을 '미수집'이라고 하면 안 된다
          row[svc.col] = got[svc.key] || '';
        });
        return row;
      });
    },
    /**
     * 매출현황(일자별) 블록. 그 블록이 쓰는 영역을 하나도 안 쓰는 계정만 있으면 만들지 않는다.
     */
    visibleSalesBlocks() {
      return SALES_BLOCKS.map((block) => {
        const areas = block.areas.filter((areaKey) => this.accountsForArea(areaKey).length > 0);
        if (!areas.length) return null;
        const columns = block.columns.filter((col) => areas.indexOf(col.from) >= 0);
        const headers = [{
          text: '일자', value: '일자', isSlot: true, align: 'start', width: '220px', sortable: false
        }];
        columns.forEach((col) => {
          headers.push({
            text: this.headerText(col.label),
            value: col.value,
            // 기준이 섞이는 표에서만 묶음 뱃지를 단다(로켓배송 = 발주일 / 판매일)
            group: col.basis || undefined,
            groupClass: col.basis === '발주일' ? 'is-blue' : 'is-green',
            isSlot: true,
            align: 'end',
            // ⚠ 정렬을 끈다. 일자 행 바로 아래에 그 날의 계정 행이 붙어 있어서
            //   정렬하면 부모와 자식이 흩어진다(표가 거짓말을 하게 된다)
            sortable: false
          });
        });
        const items = this.salesItems(block, areas);
        return {
          ...block,
          areas: areas,
          columns: columns,
          headers: headers,
          items: items,
          chartData: this.salesChart(block, items, areas),
          loading: areas.some((areaKey) => this.dailyStatus[areaKey] === 'loading')
        };
      }).filter(Boolean);
    },

    /**
     * 화면에 그리는 순서 — 묶음마다 「매출현황(+추이) → 상품·공급 통합현황」.
     * 목업의 순서이자, 읽는 순서이기도 하다: 얼마 벌었나를 먼저 보고 손볼 것을 그 다음에 본다.
     */
    pageSections() {
      const out = [];
      // 광고는 묶음 짝(매출현황)이 없어 상품·공급 자리에만 들어간다 — 맨 뒤다
      ['growth', 'rocket', 'ad'].forEach((group) => {
        const sales = this.visibleSalesBlocks.find((b) => b.key.indexOf(group + '.') === 0) || null;
        const combo = this.visibleComboBlocks.find((b) => b.key.indexOf(group + '.') === 0) || null;
        if (sales || combo) out.push({ key: group, sales: sales, combo: combo });
      });
      return out;
    },

    /**
     * 상품·공급 통합현황 블록. 두 영역 중 **하나라도** 쓰는 계정이 있으면 표를 만든다.
     * 한쪽만 쓰는 계정은 다른 쪽 칸이 「해당 없음」으로 뜬다(빈칸으로 두면 0 으로 읽힌다).
     */
    visibleComboBlocks() {
      return COMBO_BLOCKS.map((block) => {
        const parts = block.parts
          .map((part) => {
            const area = this.mergedAreas.find((a) => a.key === part.area);
            if (!area) return null;
            // keys 가 있으면 그 지표만(판매장부 리스크 9개). 없으면 영역 지표 전부
            const metrics = Array.isArray(part.keys)
              ? part.keys.map((k) => area.metrics.find((m) => m.key === k)).filter(Boolean)
              : area.metrics;
            // 기간 기준 영역은 뱃지에 기간을 붙인다 — 상태값(상품·공급)과 섞어 읽지 않게
            const group = part.periodInGroup ? part.group + ' ' + this.shortPeriodText(part.area) : part.group;
            return { ...part, group: group, areaLabel: area.label, metrics: metrics };
          })
          .filter(Boolean);

        const columns = [];
        parts.forEach((part) => {
          part.metrics.forEach((metric) => {
            columns.push({
              value: comboColumnValue(part.area, metric.key),
              areaKey: part.area,
              areaLabel: part.areaLabel,
              group: part.group,
              groupClass: part.groupClass,
              metric: metric
            });
          });
        });

        // 리스크 타일로 고른 열(이 블록에 있을 때만). 그 열을 강조하고 행을 좁힌다
        const pick = this.riskPick;
        const riskCol = pick
          ? columns.find((col) => col.areaKey === pick.areaKey && col.metric.key === pick.metricKey) || null
          : null;

        const headers = [{ text: '계정', value: '계정', isSlot: true, align: 'start', width: '260px' }];
        columns.forEach((col) => {
          const picked = !!riskCol && riskCol.value === col.value;
          headers.push({
            text: this.headerText(col.metric.label),
            value: col.value,
            group: col.group,
            groupClass: col.groupClass,
            isSlot: true,
            align: 'end',
            class: picked ? 'mad-risk-col' : '',
            cellClass: picked ? 'mad-risk-col' : ''
          });
        });

        // 두 영역 중 하나라도 쓰는 계정.
        // 행에 지표 **숫자**를 같이 싣는다 — 표시는 슬롯(cellOf)이 하지만 CDataTable 의 정렬·열 필터는
        // 행 값을 읽는다. 안 실으면 머리글을 눌러도 순서가 안 바뀐다. 값이 없는 칸(로딩·실패·해당 없음)은 null.
        const allRows = this.accounts
          .filter((account) => parts.some((part) => account.areas.indexOf(part.area) >= 0))
          .map((account) => {
            const row = { email: account.email, 계정: this.accountTitle(account) };
            columns.forEach((col) => {
              row[col.value] = this.comboCellNumber(col.areaKey, account, col.metric);
            });
            return row;
          });

        let rows = allRows;
        if (riskCol) {
          rows = allRows
            .filter((row) => Number(row[riskCol.value]) > 0)
            .sort((a, b) => Number(b[riskCol.value]) - Number(a[riskCol.value]));
        }

        return {
          ...block, parts: parts, columns: columns, headers: headers,
          rows: rows, allRowCount: allRows.length, riskCol: riskCol
        };
      }).filter((block) => block.parts.length > 0 && block.allRowCount > 0);
    },
    // 계정이 하나도 없는 영역은 표 자체를 만들지 않는다
    visibleAreas() {
      return this.mergedAreas.filter((area) => this.accountsForArea(area.key).length > 0);
    },

    /*
     * 진행 표시. 영역은 병렬로 부르고 먼저 온 것부터 채워지므로, 남은 영역만 세면 된다.
     * 계정이 없어 아예 부르지 않는 영역(loadAreaMetrics 초입에서 return)은 areaStatus 가
     * 없는 채로 남는데, 그건 '기다릴 게 없는' 것이므로 완료로 친다.
     */
    loadingAreas() {
      return this.visibleAreas.filter((area) => this.areaStatus[area.key] === 'loading');
    },
    isLoadingMetrics() {
      return this.loadingAreas.length > 0;
    },
    totalAreaCount() {
      return this.visibleAreas.length;
    },
    loadedAreaCount() {
      return this.totalAreaCount - this.loadingAreas.length;
    },
    progressPercent() {
      if (!this.totalAreaCount) return 0;
      return Math.round((this.loadedAreaCount / this.totalAreaCount) * 100);
    },
    // 남은 영역 이름. 길어지면 앞 둘만 적고 나머지는 개수로 줄인다
    loadingAreaLabels() {
      const labels = this.loadingAreas.map((area) => area.label);
      if (!labels.length) return '';
      if (labels.length <= 2) return labels.join(', ');
      return labels.slice(0, 2).join(', ') + ' 외 ' + (labels.length - 2) + '개';
    },
    // 이메일로 계정을 되찾는다. 표의 행에는 스칼라만 담고(검색·정렬이 깨지지 않게) 계정 객체는 여기서 꺼낸다
    accountByEmail() {
      const map = {};
      this.accounts.forEach((account) => {
        map[account.email] = account;
      });
      return map;
    },
    /**
     * 요약 줄 — 올릴 값이 하나라도 있는 계정만 남긴다.
     * 값 없는 항목은 라벨도 만들지 않는다(라벨만 늘어서면 요약이 아니라 잡음이다).
     */
    /**
     * 「지금 볼 것」 — 이 화면의 진입점.
     *
     * 지금까지 이 화면은 "어디부터 봐야 하나" 에 답을 안 했다. 영역 8개를 다 읽어야
     * 문제가 어디 있는지 알 수 있었다. 손봐야 할 칸만 뽑아 맨 위에 모은다.
     *
     * ⚠ 의뢰서 §7 "상태 건수들을 더해 전체 상품 수로 만들지 않는다" —
     *   **합치지 않는다.** 계정·영역·지표를 그대로 단 칸씩 나열할 뿐이다.
     *
     * 순서는 급한 것부터: 조회 실패 → 수집 필요 → 경고 지표(영역·계정 순서 그대로).
     * 경고끼리 숫자로 줄세우지 않는다 — 「판매중지 83」과 「발주불가 1,632」는
     * 단위가 달라 크기를 비교할 수 없다.
     */
    // 이메일 → 계정 순번. 색 점이 어느 표에서나 같은 색이 되게 한 곳에서만 정한다
    accountColorIndex() {
      const map = {};
      this.accounts.forEach((account, i) => { map[account.email] = i; });
      return map;
    },
    attentionChips() {
      const errors = [];
      const needs = [];
      const alerts = [];
      this.visibleAreas.forEach((area) => {
        this.accountsForArea(area.key).forEach((account) => {
          const base = {
            areaKey: area.key,
            areaLabel: area.label,
            email: account.email,
            account: this.accountTitle(account)
          };
          if (this.needOf(area.key, account)) {
            needs.push(Object.assign({ kind: 'need', text: this.needLabelOf(area.key, account) }, base));
            return;   // 아직 수집 안 한 영역의 지표는 볼 필요가 없다
          }
          let failed = false;
          area.metrics.forEach((metric) => {
            const cell = this.cellOf(area.key, account.email, metric.key);
            if (!cell) return;
            // 조회 실패는 칸마다가 아니라 영역·계정당 한 번만 알린다
            if (cell.status === 'error') { failed = true; return; }
            if (cell.status !== 'ok' || metric.tone !== 'alert') return;
            // 로켓그로스 경고는 아래 리스크 타일이 계정 수로 모아 보여준다 — 칩으로 또 늘어놓지 않는다
            if (area.key.indexOf('growth.') === 0) return;
            const bad = metric.type === 'bool' ? cell.value === true : Number(cell.value) > 0;
            if (!bad) return;
            const sub = this.cellNote(cell);
            alerts.push(Object.assign({
              kind: 'alert',
              text: metric.label + ' ' + this.formatCell(cell, metric) + (sub ? ' · ' + sub.text : '')
            }, base));
          });
          if (failed) errors.push(Object.assign({ kind: 'error', text: '조회 실패' }, base));
        });
      });
      return errors.concat(needs).concat(alerts);
    },
    // 너무 많으면 그 자체로 잡음이다. 앞쪽만 보이고 나머지는 숫자로 알린다
    attentionShown() { return this.attentionChips.slice(0, ATTENTION_CHIP_LIMIT); },
    attentionRest() { return Math.max(0, this.attentionChips.length - ATTENTION_CHIP_LIMIT); },

    /**
     * 로켓그로스 리스크 타일 — 상품관리 / 공급관리 / 판매장부 3칸.
     * 타일 하나 = 지표 하나. 주 숫자는 **그 지표가 걸린 계정 수**, 보조는 계정 합계(건수 지표만).
     * ⚠ 단위가 다른 지표끼리 더하지 않는다(의뢰서 §7). 합계는 같은 지표 안에서만 낸다.
     * ⚠ 값을 못 받은 계정(로딩·실패·수집 전)은 분모에서도 뺀다 ⇒ 「집계 N/M」로 드러낸다.
     * 판매장부는 통합현황에 실은 리스크 9개만 — 타일을 누르면 그 열로 표를 좁혀야 해서다.
     */
    growthRiskGroups() {
      const block = COMBO_BLOCKS.find((b) => b.key === 'growth.combo');
      if (!block) return [];
      return block.parts.map((part) => {
        const area = this.mergedAreas.find((a) => a.key === part.area);
        const accounts = this.accountsForArea(part.area);
        if (!area || !accounts.length) return null;
        const source = Array.isArray(part.keys)
          ? part.keys.map((k) => area.metrics.find((m) => m.key === k)).filter(Boolean)
          : area.metrics;
        const metrics = source.filter((m) => m.tone === 'alert' || m.tone === 'warn');
        if (!metrics.length) return null;
        // 값이 비어 있는(표에 '—') 지표는 모르는 것이다 — 그 지표가 전부 빈 계정은 N 에서도 뺀다
        const counted = accounts.filter((account) => this.isAreaCounted(part.area, account) &&
          metrics.some((m) => !this.isRiskCellEmpty(this.cellOf(part.area, account.email, m.key), m)));
        const tiles = metrics.map((metric) => {
          let hit = 0;
          let sum = 0;
          counted.forEach((account) => {
            const cell = this.cellOf(part.area, account.email, metric.key);
            if (this.isRiskCellEmpty(cell, metric)) return;
            const v = metric.type === 'bool' ? (cell.value === true ? 1 : 0) : Number(cell.value) || 0;
            if (v > 0) { hit += 1; sum += v; }
          });
          return { metric: metric, tone: metric.tone, accounts: hit, sum: sum };
        });
        // 걸린 계정이 많은 것부터. 0건은 자연히 뒤로 간다(흐리게는 CSS). sort 는 안정 정렬이라 동점은 정의 순서
        tiles.sort((a, b) => b.accounts - a.accounts);
        const period = part.periodInGroup ? this.periodOf(part.area) : null;
        return {
          areaKey: part.area,
          label: part.group,
          period: period ? period.start + ' ~ ' + period.end : '',
          counted: counted.length,
          total: accounts.length,
          tiles: tiles
        };
      }).filter(Boolean);
    },

  },
  mounted() {
    // 일자별 표의 기본 기간(최근 30일). 연초~오늘로 두면 첫 화면에 250줄이 쏟아진다.
    // refreshAll 이 바로 뒤에 오므로 여기서는 값만 놓고 조회는 걸지 않는다.
    SALES_BLOCKS.forEach((block) => {
      const off = this.salesEndOffset(block);
      const start = this.daysAgo(SALES_DEFAULT_DAYS - 1 + off);
      const end = this.daysAgo(off);
      block.areas.forEach((areaKey) => {
        this.$set(this.areaPeriod, areaKey,
          Object.assign(this.defaultPeriod(), { start: start, end: end }));
      });
    });
    this.refreshAll();
  },
  methods: {



    /* ── 계정별 보기 재료 ─────────────────────────────────────────
     * 「해당 없음」 = 그 계정의 areas 에 그 영역 키가 없는 경우.
     * 영역별 보기에서는 행 자체가 없던 자리라, 「값 없음(—)」과 반드시 달라 보여야 한다.
     * ──────────────────────────────────────────────────────────── */
    isAreaNotApplicable(areaKey, account) {
      if (!account || !Array.isArray(account.areas)) return true;
      return account.areas.indexOf(areaKey) === -1;
    },
    /**
     * 일자별 표의 행. 일자 행(합계) → 펼쳤으면 그 날의 계정 행이 바로 뒤에 붙는다.
     * 한 배열로 평탄화하는 이유: CDataTable 에는 행 펼치기가 없다. 대신 정렬을 꺼서
     * 부모-자식 순서가 흐트러지지 않게 잡는다(visibleSalesBlocks 의 sortable:false).
     */
    salesItems(block, areas) {
      // email -> date -> 합산 원본
      const perAccount = {};
      const dateSeen = {};
      areas.forEach((areaKey) => {
        const byEmail = this.dailyRows[areaKey] || {};
        Object.keys(byEmail).forEach((email) => {
          const rows = byEmail[email];
          if (!Array.isArray(rows)) return;       // null = 그 계정 조회 실패. 0 으로 바꾸지 않는다
          rows.forEach((row) => {
            const date = row.date;
            if (!date) return;
            dateSeen[date] = true;
            const byDate = perAccount[email] || (perAccount[email] = {});
            const bucket = byDate[date] || (byDate[date] = {});
            // 영역이 둘인 블록(로켓배송)은 같은 날에 두 영역 값이 들어온다. 열 키가 겹치지
            // 않도록 정의돼 있어 그대로 합쳐도 서로 섞이지 않는다
            Object.keys(row).forEach((key) => {
              if (key === 'date') return;
              bucket[key] = (bucket[key] || 0) + (row[key] || 0);
            });
          });
        });
      });

      const items = [];
      Object.keys(dateSeen).sort().reverse().forEach((date) => {
        const total = {};
        const children = [];
        this.accounts.forEach((account) => {
          const raw = (perAccount[account.email] || {})[date];
          if (!raw) return;                        // 그 날 값이 없는 계정은 줄을 만들지 않는다
          Object.keys(raw).forEach((key) => { total[key] = (total[key] || 0) + raw[key]; });
          children.push({
            _k: 'a|' + date + '|' + account.email,
            kind: 'account',
            date: date,
            email: account.email,
            계정: this.accountTitle(account),
            _raw: raw
          });
        });
        items.push({
          _k: 'd|' + date, kind: 'date', date: date, email: '',
          _raw: total, _children: children.length
        });
        if (this.isDateExpanded(block.key, date)) children.forEach((child) => items.push(child));
      });
      return items;
    },

    /**
     * 추이 차트 — 표와 **같은 items** 에서 만든다. 따로 조회하면 그림과 표가 어긋난다.
     * 일자 행(계정 합계)만 쓰고, 날짜는 왼쪽이 과거가 되도록 뒤집는다(표는 최신이 위다).
     */
    salesChart(block, items, areas) {
      const series = block.chart.filter((c) => {
        // 그 계정이 안 쓰는 영역의 계열은 그리지 않는다(0 만 이어진 선이 생긴다)
        const col = block.columns.find((x) => x.value === c.value);
        return !col || areas.indexOf(col.from) >= 0;
      });
      const rows = items.filter((item) => item.kind === 'date').slice().reverse();
      const categories = rows.map((item) => item.date);
      if (categories.length < 2 || !series.length) return null;

      const firstMoney = (series.find((c) => c.axis === 'money') || {}).label;
      const firstQty = (series.find((c) => c.axis === 'qty') || {}).label;
      const money = (v) => this.shortMoney(v);
      const count = (v) => this.formatValue(v, { type: 'count' });

      // 래퍼가 옵션과 series 를 **한 객체로** 받는다(ApexChart.vue). 나누면 아무것도 안 그려진다
      return {
        series: series.map((c) => ({
          name: c.label,
          type: c.type,
          data: rows.map((item) => Math.round((item._raw || {})[c.value] || 0))
        })),
        chart: {
          type: 'line', height: 260,
          toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: false }
        },
        colors: series.map((c, i) => CHART_COLORS[i % CHART_COLORS.length]),
        dataLabels: { enabled: false },
        stroke: { width: series.map((c) => (c.type === 'column' ? 0 : 2)), curve: 'smooth' },
        plotOptions: { bar: { columnWidth: '45%' } },
        legend: { position: 'top', horizontalAlign: 'left' },
        xaxis: { categories: categories, tickAmount: 10, labels: { rotate: 0 } },
        // 같은 축을 쓰는 계열은 seriesName 을 **첫 계열 이름으로 맞춰야** 눈금이 하나가 된다.
        // 각자 자기 이름을 넣으면 계열마다 축이 따로 생겨 선이 전부 같은 높이로 눕는다.
        yaxis: series.map((c) => ({
          seriesName: c.axis === 'money' ? firstMoney : firstQty,
          opposite: c.axis !== 'money',
          show: c.label === firstMoney || c.label === firstQty,
          labels: { formatter: c.axis === 'money' ? money : count },
          title: { text: c.label === firstMoney ? '금액' : (c.label === firstQty ? '수량' : undefined) }
        })),
        tooltip: {
          shared: true,
          y: {
            formatter: (v, opts) => {
              const c = series[opts && opts.seriesIndex ? opts.seriesIndex : 0];
              return c && c.axis === 'qty' ? count(v) + '개' : money(v);
            }
          }
        }
      };
    },

    // 칩이 데려갈 곳. 옛 화면은 영역 카드가 8개였지만 이제 블록이 6개라 영역 → 블록으로 접는다
    blockAnchorId(blockKey) {
      return 'mad-block-' + blockKey.replace(/[.]/g, '-');
    },
    scrollToBlock(areaKey) {
      const map = {
        'growth.ledger': 'growth.sales',
        'growth.productdb': 'growth.combo',
        'growth.scm': 'growth.combo',
        'rocket.ledger': 'rocket.sales',
        'rocket.polist': 'rocket.sales',
        'rocket.productdb': 'rocket.combo',
        'rocket.scm': 'rocket.combo',
        'ad.verify': 'ad.block'
      };
      const el = document.getElementById(this.blockAnchorId(map[areaKey] || areaKey));
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },

    // 제목 옆 기간 표기. 프리셋에 딱 맞으면 그 이름으로, 아니면 날짜를 그대로 적는다
    salesRangeText(block) {
      const presets = ['7d', '30d', '90d'];
      for (const preset of presets) {
        if (this.isSalesPresetActive(block, preset)) return this.presetLabel(preset);
      }
      const period = this.periodOf(block.areas[0]);
      return period.start + ' ~ ' + period.end;
    },
    // 계정 행은 한 단 들여 배경을 눌러 둔다 — 일자 행과 같은 무게로 보이면 합계가 어느 줄인지 흐려진다
    salesRowClass(item) {
      return item && item.kind === 'account' ? 'mad-sales-row--child' : '';
    },
    isDateExpanded(blockKey, date) {
      const map = this.expandedDates[blockKey];
      return !!(map && map[date]);
    },
    toggleDate(blockKey, date) {
      const map = Object.assign({}, this.expandedDates[blockKey] || {});
      if (map[date]) delete map[date];
      else map[date] = true;
      this.$set(this.expandedDates, blockKey, map);
    },

    // 비율은 그 날 값에서 계산한다. 미리 굳혀 두면 합계 행에서 '비율의 합' 이 되어 버린다
    salesValue(col, item) {
      const raw = (item && item._raw) || {};
      if (col.derive) return col.derive(raw);
      return raw[col.value];
    },
    salesText(col, item) {
      const value = this.salesValue(col, item);
      if (value === undefined || value === null) return '—';
      return this.formatValue(value, col);
    },
    // 값 색은 영역별 표와 같은 규칙을 쓴다(0 은 회색, 경고 지표만 강조)
    salesClass(col, item) {
      const value = this.salesValue(col, item);
      if (value === undefined || value === null) return '';
      return this.valueClass({ status: 'ok', value: value }, col);
    },

    /**
     * 일자별 값 조회. 지표(/metrics)와 따로 부른다 — 서버에서 같은 집계 캐시를 읽으므로
     * 두 번 계산되지 않고, 표마다 도착 시점이 달라도 서로를 기다리지 않는다.
     */
    async loadDaily(areaKey, force) {
      this.$set(this.dailyStatus, areaKey, 'loading');
      try {
        const period = this.periodOf(areaKey);
        const res = await axios.post('/api/dashboard/daily', {
          area: areaKey,
          start: period.start,
          end: period.end,
          basis: period.basis,
          force: !!force
        });
        if (!res.data || res.data.res !== 'success') throw new Error('daily response not success');
        this.$set(this.dailyRows, areaKey, res.data.rows || {});
      } catch (e) {
        console.error('[다계정 현황] 일자별 조회 실패', areaKey, e);
        this.$set(this.dailyRows, areaKey, {});
      } finally {
        this.$set(this.dailyStatus, areaKey, 'idle');
      }
    },

    // 블록 하나를 통째로 다시 받는다. 영역이 둘이면 둘 다 — 한쪽만 받으면 표 안에서 기준이 갈린다
    refreshSales(block) {
      block.areas.forEach((areaKey) => {
        this.loadDaily(areaKey, true);
        this.loadAreaMetrics(areaKey, true);     // 아래 줄(재고금액·매입가 미입력)도 같이 갱신
      });
    },

    // 기간은 블록 단위로 바꾼다. 그 블록이 쓰는 영역 전부에 같은 기간을 걸어야
    // 한 표 안에서 발주일 30일과 판매일 90일이 섞이는 일이 없다
    setSalesPeriod(block, patch) {
      block.areas.forEach((areaKey) => {
        this.$set(this.areaPeriod, areaKey, Object.assign({}, this.periodOf(areaKey), patch));
      });
      const first = this.periodOf(block.areas[0]);
      // 시작이 끝보다 뒤인 동안에는 조회하지 않는다(사용자가 고치는 중일 수 있다)
      if (first.start && first.end && first.start > first.end) return;
      block.areas.forEach((areaKey) => {
        this.loadDaily(areaKey);
        this.loadAreaMetrics(areaKey);
      });
    },
    // 로켓그로스 매출현황은 판매장부 화면과 같이 어제까지(오늘은 정산 전이라 비어 보인다). 그 외 블록은 오늘까지
    salesEndOffset(block) {
      return block && block.key === 'growth.sales' ? 1 : 0;
    },
    salesPresetDays(preset) {
      return { '7d': 7, '30d': 30, '90d': 90 }[preset] || 0;
    },
    applySalesPreset(block, preset) {
      const days = this.salesPresetDays(preset);
      if (!days) return undefined;
      const off = this.salesEndOffset(block);
      return this.setSalesPeriod(block, { start: this.daysAgo(days - 1 + off), end: this.daysAgo(off) });
    },
    isSalesPresetActive(block, preset) {
      const days = this.salesPresetDays(preset);
      if (!days) return false;
      const off = this.salesEndOffset(block);
      const p = this.periodOf(block.areas[0]);
      return p.start === this.daysAgo(days - 1 + off) && p.end === this.daysAgo(off);
    },
    salesPeriodInvalid(block) {
      return this.isPeriodInvalid(block.areas[0]);
    },
    // 표 아래 줄 — 날짜 축이 없는 값(재고금액·매입가 미입력)을 계정별로 적는다
    asideItems(block) {
      const out = [];
      (block.aside || []).forEach((entry) => {
        if (block.areas.indexOf(entry.area) < 0) return;
        const area = this.mergedAreas.find((a) => a.key === entry.area);
        if (!area) return;
        entry.keys.forEach((key) => {
          const metric = area.metrics.find((m) => m.key === key);
          if (!metric) return;
          const values = this.accountsForArea(entry.area).map((account) => ({
            email: account.email,
            name: this.accountTitle(account),
            text: this.formatCell(this.cellOf(entry.area, account.email, key), metric)
          }));
          if (values.length) out.push({ key: entry.area + '|' + key, label: metric.label, values: values });
        });
      });
      return out;
    },

    /**
     * 합친 표의 기준 시각 — 두 영역 중 **더 오래된 쪽**을 적는다.
     * "이 표는 최소 이만큼 최신" 이 맞는 표현이다(영역별 표에서 쓰던 규칙과 같다).
     */
    comboBuiltAt(block) {
      let oldestArea = '';
      let oldest = '';
      block.parts.forEach((part) => {
        const at = this.areaBuiltAt[part.area];
        if (!at) return;
        if (!oldest || at < oldest) { oldest = at; oldestArea = part.area; }
      });
      return oldestArea ? this.builtAtText(oldestArea) : '';
    },
    // 그 블록에서 기간을 쓰는 첫 영역(광고 성과만 해당). 없으면 날짜 칸을 그리지 않는다
    comboPeriodArea(block) {
      // periodInGroup 영역(판매장부)은 매출현황의 기간을 따른다 — 여기 날짜 칸을 또 두지 않는다
      const part = block.parts.find((p) => !p.periodInGroup && !!this.periodSpec(p.area));
      return part ? part.area : '';
    },
    // 합친 표는 영역 둘을 같이 다시 부른다(한쪽만 새로 받으면 한 표 안에서 시각이 갈린다)
    refreshCombo(block) {
      block.parts.forEach((part) => this.loadAreaMetrics(part.area, true));
    },
    // 통합현황 행에 싣는 정렬용 숫자. 화면 표시는 여전히 슬롯이 cellOf 로 그린다
    comboCellNumber(areaKey, account, metric) {
      if (this.isAreaNotApplicable(areaKey, account) || this.needOf(areaKey, account)) return null;
      const cell = this.cellOf(areaKey, account.email, metric.key);
      if (cell.status !== 'ok' || cell.value === null || cell.value === undefined) return null;
      if (metric.type === 'bool') return cell.value === true ? 1 : 0;
      const n = Number(cell.value);
      return Number.isFinite(n) ? n : null;
    },
    // 리스크 타일 집계에 넣을 계정인가 — 값을 다 받은 계정만(로딩·실패·수집 전은 모른다)
    isAreaCounted(areaKey, account) {
      if (this.needOf(areaKey, account)) return false;
      const bucket = this.metricCells[areaKey + '|' + account.email];
      if (!bucket) return false;
      return !Object.values(bucket).some((cell) => cell && (cell.status === 'loading' || cell.status === 'error'));
    },
    // 리스크 집계에서 「값 없음」으로 볼 셀 — 받지 못했거나 null/빈값/숫자 아님
    isRiskCellEmpty(cell, metric) {
      if (!cell || cell.status !== 'ok') return true;
      if (cell.value === null || cell.value === undefined || cell.value === '') return true;
      if (metric.type === 'bool') return false;
      return !isFinite(Number(cell.value));
    },
    isRiskPicked(areaKey, metricKey) {
      return !!(this.riskPick && this.riskPick.areaKey === areaKey && this.riskPick.metricKey === metricKey);
    },
    // 같은 타일을 다시 누르면 푼다. 고르면 표로 데려간다
    toggleRiskPick(areaKey, metricKey) {
      if (this.isRiskPicked(areaKey, metricKey)) {
        this.riskPick = null;
        return;
      }
      this.riskPick = { areaKey: areaKey, metricKey: metricKey };
      this.$nextTick(() => {
        const el = document.getElementById(this.blockAnchorId('growth.combo'));
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
    clearRiskPick() {
      this.riskPick = null;
    },
    // 묶음 뱃지에 붙일 짧은 기간(MM-DD~MM-DD). 뱃지 폭이 좁아 연도는 뺀다
    shortPeriodText(areaKey) {
      const p = this.periodOf(areaKey);
      return String(p.start || '').slice(5) + '~' + String(p.end || '').slice(5);
    },
    areaOf(areaKey) {
      return this.mergedAreas.find((item) => item.key === areaKey) || null;
    },


    accountDot(email) {
      const idx = this.accountColorIndex[email];
      const color = ACCOUNT_DOT_COLORS[(idx === undefined ? 0 : idx) % ACCOUNT_DOT_COLORS.length];
      return { backgroundColor: color };
    },
    chipAreaLabel(label) {
      const parts = String(label || '').split('>');
      if (parts.length < 2) return String(label || '').trim();
      const head = parts[0].trim().replace('로켓그로스', '그로스').replace('로켓배송', '배송');
      return head + ' ' + parts[1].trim();
    },
    builtAtText(areaKey) {
      const iso = this.areaBuiltAt[areaKey];
      if (!iso) return '';
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      const p = (n) => String(n).padStart(2, '0');
      const hm = p(d.getHours()) + ':' + p(d.getMinutes());
      const now = new Date();
      const sameDay = d.getFullYear() === now.getFullYear()
        && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
      return sameDay ? hm + ' 조회' : (d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate()) + ' ' + hm + ' 조회');
    },
    headerText(label) {
      const s = String(label || '');
      if (s.length <= 7) return s;
      const paren = s.indexOf('(');
      if (paren > 0) return s.slice(0, paren) + '\n' + s.slice(paren);
      const space = s.lastIndexOf(' ');
      if (space > 0) return s.slice(0, space) + '\n' + s.slice(space + 1);
      return s;
    },
    // 행에는 이메일만 있으므로 계정 객체는 여기서 꺼낸다(없으면 빈 객체 — 템플릿이 죽지 않게)
    accountOf(row) {
      return (row && this.accountByEmail[row.email]) || {};
    },



    // 표시 이름은 「업체아이디 (담당자 이름)」. 담당자 이름만 쓰면 계정마다 사람 이름이라
    // 어느 업체인지 구분이 안 된다 — 업체를 가리키는 값은 companyid 쪽이다.
    accountTitle(account) {
      const company = account.companyid || '';
      const person = account.name || '';
      if (company && person) return company + ' (' + person + ')';
      return company || person || account.email || '';
    },
    // 수집 현황 표 계정 칸의 둘째 줄. 이메일과 사용 서비스를 **한 줄에** 둔다 —
    // 따로 줄을 내면 세 줄이 되어 행 높이(40px)를 넘어간다.
    collectMetaOf(row) {
      const uses = this.usesLabelOf(this.accountOf(row));
      return uses ? row.email + ' · ' + uses : row.email;
    },
    /**
     * 계정 순서는 서버가 준 순서 그대로 유지한다(모든 표에서 동일).
     * 그 계정이 areas 에 갖고 있지 않은 영역이면 행 자체를 만들지 않는다.
     */
    accountsForArea(areaKey) {
      return this.accounts.filter(
        (account) => Array.isArray(account.areas) && account.areas.indexOf(areaKey) !== -1
      );
    },
    /**
     * 그 계정의 그 영역이 "아직 수집 전" 인지. 서버가 준 needs 를 그대로 믿는다.
     * needs 는 그 계정이 실제로 쓰는 영역만 담고, 판정 대상 영역은 앞으로 늘어난다 —
     * 그래서 영역 키를 여기에 박지 않고 배열을 그대로 훑는다.
     * needs 가 없거나 비면(옛 서버/해당 없음) null 이라 기존 동작 그대로다.
     */
    needOf(areaKey, account) {
      if (!account || !Array.isArray(account.needs)) return null;
      return account.needs.find((item) => item && item.area === areaKey) || null;
    },
    needLabelOf(areaKey, account) {
      const need = this.needOf(areaKey, account);
      return (need && need.label) || '수집 필요';
    },
    cellOf(areaKey, email, metricKey) {
      const bucket = this.metricCells[areaKey + '|' + email];
      if (!bucket) return EMPTY_CELL;
      return bucket[metricKey] || EMPTY_CELL;
    },
    /**
     * 셀 글자 클래스. 상태(정상0·경고)에 더해 **위계**를 얹는다.
     *
     * 지금까지는 총매출액 1.8억과 부가세 558만이 같은 크기·같은 색이었다. 한 줄에 11개 숫자가
     * 같은 무게로 늘어서면 어디를 봐야 하는지가 안 보인다. 그래서 영역마다
     * '이 영역을 대표하는 두 숫자'(emphasis:'strong')만 굵게 키우고, 파생·보조 지표
     * (emphasis:'muted', 예: 부가세)는 한 단계 눌러 둔다. 나머지는 그대로다.
     * ⚠ 숫자·문구는 건드리지 않는다. 굵기와 색만 바꾼다.
     */
    valueClass(cell, metric) {
      const rank = metric.emphasis === 'strong' ? ' multi-account-dashboard__cell-value--strong'
        : metric.emphasis === 'muted' ? ' multi-account-dashboard__cell-value--muted' : '';
      if (metric.type === 'bool') {
        const bad = metric.tone === 'alert' ? cell.value === true : cell.value === false;
        return (bad ? 'multi-account-dashboard__cell-value--alert' : 'multi-account-dashboard__cell-value--zero') + rank;
      }
      // 정상 0 은 회색이 우선이다 — 0 을 굵게 키우면 '많다'로 읽힌다
      if (cell.value === 0) return 'multi-account-dashboard__cell-value--zero';
      // 「계산 준비 중」 은 경고가 아니다 — 빨갛게 칠하면 문제가 있는 것으로 읽힌다
      if (cell.note && cell.note.kind === 'recon' && cell.note.preparing) return 'multi-account-dashboard__cell-value--zero';
      if (metric.tone === 'alert') return 'multi-account-dashboard__cell-value--alert' + rank;
      if (metric.tone === 'warn') return 'multi-account-dashboard__cell-value--warn' + rank;
      return rank.trim();
    },
    /**
     * 금액을 짧게. 보조줄은 자리가 없어서 `12,400,000원` 을 그대로 두면 값보다 길어진다.
     * ⚠ **지표 값 자체에는 쓰지 않는다** — 의뢰서 단위 규칙은 표의 금액 칸에 그대로 적용된다.
     *   여기서 줄이는 건 값 아래 딸린 보조 표기뿐이다.
     */
    shortMoney(n) {
      const v = Math.round(Number(n) || 0);
      if (Math.abs(v) >= 100000000) return (v / 100000000).toFixed(v % 100000000 === 0 ? 0 : 1) + '억';
      if (Math.abs(v) >= 10000) return Math.round(v / 10000).toLocaleString('ko-KR') + '만';
      return v.toLocaleString('ko-KR');
    },
    /**
     * 값 아래 딸리는 보조줄. 지금은 「구매필요」의 **발주 필요금액**뿐이다.
     * 「구매필요 8」 만으로는 8개를 사는 데 얼마가 드는지 모른다.
     * ⚠ 손실을 추정하지 않는다 — 재입고 리드타임을 모르므로 '품절 예상 손실' 같은 건 안 만든다.
     *   소요 자금은 가정 없이 정확하다.
     * 매입가를 모르는 상품이 섞였으면 그 사실을 같이 말한다(조용히 작은 금액을 보여주지 않는다).
     */
    cellNote(cell) {
      const n = cell && cell.note;
      // 「확인요청 대상」 — 원가 금액. 정확 모드로 계산된 상품만(공급관리와 같은 숫자). 범위는 보여주지 않는다
      if (n && typeof n === 'object' && n.kind === 'recon') {
        if (n.preparing || !(n.amount > 0)) return null;
        const amt = Math.round(n.amount || 0);
        return {
          text: this.shortMoney(amt) + '원',
          title: '90일 넘게 확인 안 된 재고의 매입원가 ' + amt.toLocaleString('ko-KR') + '원'
            + (n.unpriced > 0 ? ' (매입가를 모르는 상품 ' + n.unpriced + '개는 금액에서 빠져 있습니다)' : '')
        };
      }
      if (!n || typeof n !== 'object' || !(n.amount > 0)) return null;
      return {
        text: this.shortMoney(n.amount) + '원',
        title: '발주에 필요한 금액 ' + Math.round(n.amount).toLocaleString('ko-KR') + '원'
          + (n.unpriced > 0 ? ' (매입가를 모르는 상품 ' + n.unpriced + '개는 빠져 있습니다)' : '')
      };
    },
    // cell.note = 값 옆에 붙는 보조 수치. '있음' 만으로는 22개인지 1개인지 모른다 —
    // 원본 화면도 건수로 말한다(growthSalesLedger.vue:154 "매입가가 없는 상품 N개").
    formatCell(cell, metric) {
      // 확인요청 대상 — 공급관리와 같은 표기. 정확히 계산된 상품이 아직 없으면 「계산 준비 중」
      const rn = cell && cell.note;
      if (rn && typeof rn === 'object' && rn.kind === 'recon' && rn.preparing) return '계산 준비 중';
      const base = this.formatValue(cell.value, metric);
      if (metric.type === 'bool' && cell.value === true && typeof cell.note === 'number' && cell.note > 0) {
        return base + ' ' + Number(cell.note).toLocaleString('ko-KR') + '건';
      }
      return base;
    },
    formatValue(value, metric) {
      if (metric.type === 'bool') {
        return value ? (metric.trueLabel || '예') : (metric.falseLabel || '아니오');
      }
      // 숫자가 아닌 기준 표기(광고 '분석기간' 등). Number() 를 태우면 NaN 이라 '—' 가 돼버린다.
      if (metric.type === 'text') return (value === null || value === undefined || value === '') ? '—' : String(value);
      const num = Number(value);
      if (!isFinite(num)) return '—';
      if (metric.type === 'percent') return num.toLocaleString('ko-KR') + '%';
      // 원 단위 금액에 소수점이 보이면 안 된다. 합계 정확도 때문에 원본 값은 실수 그대로 두고
      // 표시할 때만 반올림한다.
      if (metric.type === 'money') return Math.round(num).toLocaleString('ko-KR') + '원';
      return num.toLocaleString('ko-KR');
    },

    // ───── 조회 기간 (의뢰서 §4) ─────────────────────────────────
    //
    // 날짜는 전부 KST 기준 'YYYY-MM-DD' 문자열로 다룬다. Date 를 toISOString() 하면 UTC 로
    // 밀려 한국 새벽에 하루 전 날짜가 나온다 — 그래서 로컬 연/월/일을 직접 조립한다.
    ymd(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return y + '-' + m + '-' + day;
    },
    daysAgo(n) {
      const d = new Date();
      d.setDate(d.getDate() - n);
      return this.ymd(d);
    },
    // 서버 기본값과 같은 연초~오늘. 여기서 바꾸면 서버(/api/dashboard/metrics)도 같이 바꿔야 한다
    defaultPeriod() {
      const today = this.ymd(new Date());
      return { start: today.slice(0, 4) + '-01-01', end: today, basis: '발주일시' };
    },
    periodOf(areaKey) {
      return this.areaPeriod[areaKey] || this.defaultPeriod();
    },
    // 이 영역이 기간을 쓰는가. 상품관리·공급관리 4개는 '조회 시점의 상태'라 기간이 없다.
    periodSpec(areaKey) {
      const area = AREA_DEFS.find((a) => a.key === areaKey);
      return (area && area.period) || null;
    },
    setPeriod(areaKey, patch) {
      const next = Object.assign({}, this.periodOf(areaKey), patch);
      // 시작이 끝보다 뒤면 서버가 빈 결과를 준다. 값을 조용히 뒤집지 말고 붙잡아 둔다 —
      // 사용자가 시작일을 먼저 고치는 중일 수 있어서, 뒤집힌 동안에는 조회하지 않는다.
      this.$set(this.areaPeriod, areaKey, next);
      if (next.start && next.end && next.start > next.end) return;
      this.loadAreaMetrics(areaKey);
    },
    presetLabel(preset) {
      return { today: '오늘', '7d': '최근 7일', '30d': '최근 30일', '90d': '최근 90일', '1m': '최근 1개월' }[preset] || preset;
    },
    // 이 프리셋이 지금 걸려 있는가 — 눌린 상태를 보여줘야 어떤 기준인지 안다
    isPresetActive(areaKey, preset) {
      const p = this.periodOf(areaKey);
      const today = this.ymd(new Date());
      if (preset === 'today') return p.start === today && p.end === today;
      if (preset === '7d') return p.start === this.daysAgo(6) && p.end === today;
      if (preset === '30d') return p.start === this.daysAgo(29) && p.end === today;
      if (preset === '90d') return p.start === this.daysAgo(89) && p.end === today;
      if (preset === '1m') {
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        return p.start === this.ymd(d) && p.end === today;
      }
      return false;
    },
    isPeriodInvalid(areaKey) {
      const p = this.periodOf(areaKey);
      return !!(p.start && p.end && p.start > p.end);
    },

    /**
     * force=true 면 서버 캐시를 건너뛰고 원본을 다시 읽는다.
     * 첫 진입(mounted)은 force 를 주지 않는다 — 매번 주면 90초 캐시가 무의미해져
     * 재방문이 첫 방문만큼 느려진다. 사용자가 「전체 새로고침」을 누른 때만 뚫는다
     * (의뢰서 §5 "필수 기능은 전체 새로고침" = 낡은 값을 강제로 걷어낼 길이 있어야 한다).
     */
    async refreshAll(force) {
      await this.loadAccounts();
      this.loadCollect();
      this.visibleAreas.forEach((area) => {
        this.loadAreaMetrics(area.key, force);
      });
      // 일자별 표. 계정 목록을 받은 뒤라야 어떤 영역을 부를지 알 수 있다
      const dailyAreas = {};
      SALES_BLOCKS.forEach((block) => {
        block.areas.forEach((areaKey) => {
          if (this.accountsForArea(areaKey).length) dailyAreas[areaKey] = true;
        });
      });
      Object.keys(dailyAreas).forEach((areaKey) => this.loadDaily(areaKey, force));
    },

    /**
     * 계정별 수집 현황. 지표와 따로 부른다 — 지표는 영역마다 무겁고 이건 존재 확인뿐이라
     * 같이 묶으면 가벼운 표가 무거운 조회를 기다린다.
     */
    async loadCollect() {
      try {
        const res = await axios.post('/api/dashboard/collect', {});
        if (!res.data || res.data.res !== 'success') {
          console.error('[다계정 현황] 수집 현황 조회 실패', res.data && res.data.alert);
          return;
        }
        this.collectServices = (res.data.services || []).map((svc) => ({
          key: svc.key,
          label: svc.label,
          basis: svc.basis,
          staleDays: svc.staleDays || 0,
          // 열 키 — 점을 빼서 Vuetify 가 경로로 파고들지 않게 한다(collectHeaders 주석 참고)
          col: 'c_' + String(svc.key).replace(/\./g, '_')
        }));
        this.collect = res.data.collect || {};
        this.collectLast = res.data.lastAt || {};
      } catch (e) {
        console.error('[다계정 현황] 수집 현황 조회 실패', e);
      }
    },

    // 그 계정이 어떤 서비스를 쓰는지 한 줄로. 아래 「해당 없음」 칸들의 이유가 된다
    usesLabelOf(account) {
      const areas = (account && account.areas) || [];
      const growth = areas.some((k) => k.indexOf('growth.') === 0);
      const rocket = areas.some((k) => k.indexOf('rocket.') === 0);
      if (growth && rocket) return '그로스·로켓 연결';
      if (growth) return '그로스만 연결';
      if (rocket) return '로켓만 연결';
      return '';
    },
    // 그 칸의 마지막 날짜 정보. 서버가 안 준 칸(옛 응답·조회 실패)은 null 이다
    collectAt(item, svc) {
      const byAccount = this.collectLast[item.email] || {};
      return byAccount[svc.key] || null;
    },
    /**
     * 칸에 적을 글자. **날짜를 아는 칸은 날짜가 답이다** —
     * 「수집완료」는 그게 오늘 것인지 3년 전 것인지를 말해주지 못한다.
     * 날짜를 모르는 칸(수집 시각을 안 남기던 컬렉션)만 예전 글자로 둔다.
     */
    collectLabel(state, at) {
      if (state === 'na') return '해당 없음';
      if (state === 'error') return '확인불가';
      if (at && at.date) return at.date;
      if (state === 'collected') return '수집완료';
      if (state === 'none') return '미수집';
      return '…';
    },
    // 받아두긴 했는데 한참 밀린 칸을 초록으로 두지 않는다 — 초록은 '괜찮다'로 읽힌다
    collectChipClass(item, svc) {
      const state = item[svc.col] || 'none';
      const at = this.collectAt(item, svc);
      if (state === 'collected' && at && at.stale) return 'mad-chip--stale';
      return 'mad-chip--' + state;
    },
    collectTitle(svc, state, at) {
      if (state === 'na') return '이 계정은 ' + svc.label + ' 를 쓰지 않습니다';
      if (state === 'error') return '조회에 실패했습니다 — 미수집인지 아닌지 알 수 없습니다';
      const basis = svc.basis === 'today'
        ? '오늘 수집했는지로 판정합니다(원본 화면의 수집 버튼과 같은 기준)'
        : '원본을 한 번이라도 받아뒀는지로 판정합니다';
      const lines = [svc.label + ' — ' + basis];
      if (at && at.date) {
        lines.push('데이터가 있는 마지막 날: ' + at.date);
        if (at.stale) lines.push('※ ' + svc.staleDays + '일 넘게 밀려 있습니다 — 수집이 멈췄는지 확인해 주세요');
      } else {
        lines.push('마지막 날짜를 알 수 없습니다 — 다음 수집부터 표시됩니다');
      }
      return lines.join('\n');
    },

    async loadAccounts() {
      if (this.loading) return;
      this.loading = true;
      this.loadError = '';
      try {
        const res = await axios.post('/api/dashboard/accounts', {});
        if (!res.data || res.data.res !== 'success') {
          this.accounts = [];
          this.serverAreas = [];
          this.loadError = (res.data && res.data.alert) || '계정 정보를 불러오지 못했습니다.';
          return;
        }
        this.accounts = Array.isArray(res.data.accounts) ? res.data.accounts : [];
        this.serverAreas = Array.isArray(res.data.areas) ? res.data.areas : [];
        this.viewerCanSeeRecon = res.data.canSeeRecon === true;
      } catch (e) {
        console.error('[다계정 현황] 계정 목록 조회 실패', e);
        this.accounts = [];
        this.serverAreas = [];
        this.loadError = '계정 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.';
      } finally {
        this.loading = false;
      }
    },

    /**
     * 지표 값 채우기. 지금은 가상값이고, 실제 API 가 생기면
     * METRIC_API_READY 만 켜면 아래 분기가 서버 호출로 바뀐다.
     */
    async loadAreaMetrics(areaKey, force) {
      const accounts = this.accountsForArea(areaKey);
      if (!accounts.length) return;

      this.$set(this.areaStatus, areaKey, 'loading');
      accounts.forEach((account) => {
        this.$set(this.metricCells, areaKey + '|' + account.email, this.loadingCells(areaKey));
      });

      try {
        let payload;
        if (METRIC_API_READY) {
          // 기간은 영역마다 다르다. 기간을 안 쓰는 영역(상품관리·공급관리)도 보내기는 하되
          // 서버가 무시한다 — 그쪽은 '조회 시점의 상태' 라서 기간 개념이 없다(의뢰서 §4).
          const period = this.periodOf(areaKey);
          const res = await axios.post(METRIC_API_URL, {
            area: areaKey,
            emails: accounts.map((account) => account.email),
            start: period.start,
            end: period.end,
            basis: period.basis,
            force: !!force
          });
          if (!res.data || res.data.res !== 'success') throw new Error('metrics response not success');
          payload = res.data.metrics || {};
          // 이 표의 숫자가 언제 만들어졌나. 계정마다 다를 수 있어(캐시 적중 여부) **가장 오래된 것**을
          // 적는다 — "이 표는 최소 이만큼 최신" 이 맞는 표현이다.
          // ⚠ '원본 수집 시각'이 아니라 '마지막 조회(집계) 시각'이다(의뢰서 §4 가 가른 셋 중 하나).
          const stamps = Object.values(res.data.builtAt || {}).filter(Boolean).sort();
          this.$set(this.areaBuiltAt, areaKey, stamps.length ? stamps[0] : '');
          // 아직 지표를 만들지 않은 영역은 '실패'가 아니라 '값 없음'이다.
          // 여기서 errorCells 로 떨어뜨리면 멀쩡한 화면이 빨갛게 보인다.
          if (res.data.implemented === false) {
            accounts.forEach((account) => {
              this.$set(this.metricCells, areaKey + '|' + account.email, this.emptyCells(areaKey));
            });
            this.$set(this.areaStatus, areaKey, 'ready');
            return;
          }
        } else {
          payload = buildMockAreaMetrics(areaKey, accounts);
        }

        accounts.forEach((account) => {
          const cells = payload[account.email] || this.errorCells(areaKey);
          this.$set(this.metricCells, areaKey + '|' + account.email, cells);
        });
      } catch (e) {
        console.error('[다계정 현황] 지표 조회 실패', areaKey, e);
        accounts.forEach((account) => {
          this.$set(this.metricCells, areaKey + '|' + account.email, this.errorCells(areaKey));
        });
      } finally {
        this.$set(this.areaStatus, areaKey, 'idle');
      }
    },

    fillCells(areaKey, status) {
      const area = AREA_DEFS.find((item) => item.key === areaKey);
      const cells = {};
      if (!area) return cells;
      area.metrics.forEach((metric) => {
        cells[metric.key] = { status: status, value: null };
      });
      return cells;
    },
    loadingCells(areaKey) {
      return this.fillCells(areaKey, 'loading');
    },
    errorCells(areaKey) {
      return this.fillCells(areaKey, 'error');
    },
    // 아직 지표를 만들지 않은 영역용. '실패'와 구분해야 화면이 잘못 빨개지지 않는다
    emptyCells(areaKey) {
      return this.fillCells(areaKey, 'empty');
    },

    /**
     * 계정 바로가기 — 계정을 먼저 바꾸고 그 영역 화면으로 간다.
     * 전환이 실패하면 이동하지 않는다(남의 계정 화면을 보게 되면 안 된다).
     * 전환 중에는 다른 바로가기를 모두 잠가 요청이 겹치지 않게 한다.
     */
    async goToArea(area, account, query) {
      if (this.switchingEmail) return;
      // query 가 있으면 그 화면의 필터까지 걸어서 연다(예: 공급관리 미확인 목록)
      const target = query ? { name: area.route, query: query } : { name: area.route };

      if (account.isCurrent) {
        this.$router.push(target).catch(() => {});
        return;
      }

      this.switchingEmail = account.email;
      try {
        const res = await axios.post('/api/accountLink/switch', { email: account.email });
        if (!res.data || res.data.res !== 'success') {
          alert((res.data && res.data.alert) || '계정 전환에 실패했습니다. 이동하지 않습니다.');
          return;
        }
        // ⚠ 전환 뒤에는 $router.push 로 가면 안 된다.
        // Vuex 에 **이전 계정** 데이터가 그대로 남아 있어서 새 계정 화면이 옛 숫자를 그리고,
        // 라우터 가드도 옛 권한으로 판정한다. 헤더의 계정전환(HeaderStyle1.vue)도 같은 이유로
        // 전환 후 window.location.reload() 로 문서를 통째로 다시 띄운다.
        window.location.assign(this.$router.resolve(target).href);
      } catch (e) {
        console.error('[다계정 현황] 계정 전환 실패', e);
        alert('계정 전환에 실패했습니다. 이동하지 않습니다.');
      } finally {
        this.switchingEmail = '';
      }
    }
  }
};
</script>

<style lang="scss" scoped>
$mad-border: #e6e6e6;
$mad-head-bg: #f7f8fa;

.multi-account-dashboard {
  &__nav-card {
    .card-body {
      padding: 0.75rem 1rem;
    }
  }

  &__nav {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem 1.2rem;
  }

  // 바로가기도 그룹 단위로 묶어 보인다.
  // 칩 이름에서 그룹 접두사를 뺐으므로, 경계는 묶음 상자가 대신 보여준다
  &__nav-group {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.35rem;
    padding: 0.15rem 0.5rem 0.15rem 0.4rem;
    border: 1px solid $mad-border;
    border-radius: 0.4rem;
    background: $mad-head-bg;
  }

  &__nav-group-label {
    color: #666;
    font-size: 0.75rem;
    font-weight: 700;
  }

  &__nav-link {
    display: inline-block;
    padding: 0.2rem 0.6rem;
    border: 1px solid $mad-border;
    border-radius: 1rem;
    font-size: 0.82rem;
    color: #444;
    background: #fff;

    &:hover {
      background: $mad-head-bg;
      text-decoration: none;
    }
  }

  &__legend {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-top: 0.6rem;
    font-size: 0.78rem;
    color: #777;
  }

  &__legend-item {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }

  // 표 위 한 줄 요약 — 표를 대신하지 않고 위에 얹는다
  &__summary-card {
    .card-body {
      padding: 0.6rem 1rem;
    }
  }

  &__summary-title {
    margin-bottom: 0.35rem;
    font-size: 0.78rem;
    font-weight: 600;
    color: #777;
  }

  /* ── 계정별 한 줄 요약 = 계정 카드 × 지표 타일 ──────────────────
   * 계정이 6~8개면 글자 나열로는 어느 숫자가 어느 계정 것인지 눈으로 못 따라간다.
   * 계정 경계를 카드로 먼저 긋고, 그 안에서 지표를 타일로 비교하게 한다.
   * 카드는 넓이에 따라 접히고(auto-fill), 계정이 둘이면 둘만 나란히 선다.
   * ──────────────────────────────────────────────────────────── */
  &__kpi-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 0.5rem;
  }

  &__kpi-card {
    padding: 0.5rem 0.7rem;
    border: 1px solid $mad-border;
    border-radius: 0.4rem;
    background: #fff;
  }

  &__kpi-name {
    margin-bottom: 0.4rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.8rem;
    font-weight: 700;
    color: #1b2a3a;
  }

  &__kpi-tiles {
    display: flex;
    gap: 0.4rem;
  }

  &__kpi-tile {
    flex: 1 1 0;
    min-width: 0;
    padding: 0.35rem 0.5rem;
    border-radius: 0.3rem;
    background: $mad-head-bg;
  }

  &__kpi-label {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.7rem;
    color: #8a9099;
  }

  /* 추이선. 눈금도 축도 없다 — 여기서 읽는 건 값이 아니라 모양이다.
   * 세로 기준은 계정마다 따로 잡으므로 계정끼리 높이를 비교하면 안 된다(그래서 축을 안 그린다). */
  &__spark {
    display: block;
    width: 100%;
    height: 26px;
    margin-top: 0.35rem;
    overflow: visible;

    polyline {
      fill: none;
      stroke: #3572b0;
      stroke-width: 1.4;
      stroke-linejoin: round;
      stroke-linecap: round;
      vector-effect: non-scaling-stroke;
    }
  }

  &__kpi-value {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
    font-size: 0.95rem;
    font-weight: 700;
    color: #1b2a3a;
    line-height: 1.35;
  }

  // 그룹 제목 줄 = 그룹 통째로 접기 버튼
  &__group {
    margin-bottom: 0.5rem;
  }

  &__group-head {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    width: 100%;
    margin-bottom: 0.5rem;
    padding: 0.35rem 0.6rem;
    border: 1px solid $mad-border;
    border-radius: 0.35rem;
    background: $mad-head-bg;
    color: inherit;
    text-align: left;

    &:hover {
      background: #eef1f6;
    }

    &:focus {
      outline: 2px solid #9ec5fe;
      outline-offset: 2px;
    }
  }

  &__group-caret {
    width: 0.9rem;
    color: #888;
    font-size: 0.8rem;
  }

  &__group-title {
    font-size: 1rem;
    font-weight: 700;
  }

  &__group-count {
    color: #888;
    font-size: 0.78rem;
  }


  &__area-toggle {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0;
    border: 0;
    background: none;
    color: inherit;
    text-align: left;

    &:focus {
      outline: 2px solid #9ec5fe;
      outline-offset: 2px;
    }
  }

  &__area-caret {
    width: 0.9rem;
    color: #888;
    font-size: 0.8rem;
  }

  &__area-title {
    font-size: 1.05rem;
    font-weight: 600;
  }

  &__area-note {
    padding: 0 0.35rem;
    border: 1px solid $mad-border;
    border-radius: 0.25rem;
    background: $mad-head-bg;
    color: #999;
    font-size: 0.7rem;
    font-weight: 500;
  }

  // 조회 기준 줄. 표 바로 위에 붙어야 "이 표가 어느 기간인지"로 읽힌다 —
  // 떨어뜨려 놓으면 화면 전체의 기간으로 오해한다.
  /* 기준줄은 이제 영역 헤더 **안에** 있다. 따로 상자를 두르면 한 줄에 상자가 두 개가 된다.
   * 테두리·배경·아래여백을 벗기고 헤더의 일부처럼 흐르게 둔다.
   * 실측: 영역당 한 줄(약 60px)이 사라진다 — 8영역이면 ~480px. */
  &__period {
    display: flex;
    flex: 1 1 auto;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.25rem;
    min-width: 0;
    margin: 0 0.5rem;
    font-size: 0.8rem;
  }

  &__period-label {
    margin-right: 0.35rem;
    color: #666;
    font-weight: 600;
  }

  &__period-date {
    width: 9.5rem;
  }

  &__period-month {
    width: 8.5rem;
  }

  // 기간을 안 쓰는 영역(상품관리·공급관리)에 붙는 설명
  &__period-note {
    color: #888;
  }

  &__period-warn {
    margin-left: 0.5rem;
    color: #c0392b;
    font-weight: 600;
  }

  /* ── 「지금 볼 것」 ─────────────────────────────────────────────
   * 손봐야 할 칸만 모은 줄. 이 화면의 진입점이라 맨 위에 두고, 숫자보다 먼저 보이게 한다.
   * 칩 색은 아래 표의 상태 색과 같게 맞춘다 — 같은 뜻에 다른 색을 쓰면 다시 배워야 한다.
   * ──────────────────────────────────────────────────────────── */
  &__attention-card {
    .card-body { padding: 0.5rem 0.8rem; }
  }

  &__attention {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.3rem;
  }

  &__attention-title {
    margin-right: 0.35rem;
    font-size: 0.78rem;
    font-weight: 600;
    color: #777;

    b { color: #d9534f; }
  }

  &__attention-chip {
    display: inline-flex;
    align-items: baseline;
    gap: 0.25rem;
    max-width: 22rem;
    overflow: hidden;
    padding: 0.12rem 0.5rem;
    border: 1px solid #f0c8c6;
    border-radius: 999px;
    background: #fdf3f2;
    color: #b5413d;
    font-size: 0.73rem;
    line-height: 1.6;
    white-space: nowrap;

    &:hover { border-color: #d9534f; background: #fbe7e6; }

    &--need { border-color: #f0d9a8; background: #fdf7ea; color: #98700f; }
    &--error { border-color: #d9534f; background: #d9534f; color: #fff; }
  }

  // 계정 고정 색 — 같은 계정은 어느 표에서도 같은 점
  &__account-dot {
    flex: 0 0 auto;
    width: 7px;
    height: 7px;
    margin-right: 0.3rem;
    border-radius: 50%;
  }

  &__attention-acc { font-weight: 700; }
  // 영역은 '어디로 갈지' 를 알려주는 길잡이라 지표보다 연하게
  &__attention-area { opacity: 0.65; font-size: 0.68rem; }

  &__attention-rest {
    font-size: 0.73rem;
    color: #9aa0a6;
  }

  /* 영역 헤더를 상단에 붙여 둔다. 8영역을 내려가며 값을 비교할 때
   * "지금 어느 영역 숫자인가" 가 사라지는 게 제일 헷갈린다.
   * top 은 앱 상단바(.iq-top-navbar, fixed 73px) 아래. */
  // 영역 제목 줄 = 접기 버튼. 위 주석대로 상단에 붙여 둔다
  &__area-head {
    position: sticky;
    top: 73px;
    z-index: 3;
    margin-bottom: 0.4rem;
    padding: 0.25rem 0;
    background: #fff;
  }

  // 접힌 열 안내. 정보를 숨긴 자리라 '있다'는 건 보이되 숫자보다는 뒤에
  &__zero-chip {
    margin-right: 0.35rem;
    padding: 0.1rem 0.45rem;
    border: 1px dashed $mad-border;
    border-radius: 999px;
    background: transparent;
    color: #8a9099;
    font-size: 0.72rem;
    line-height: 1.6;

    &:hover { border-style: solid; color: #4a5560; }
  }

  // 영역마다 반복되는 보조 동작. 숫자보다 먼저 눈에 띄면 안 된다
  &__area-refresh {
    padding: 0.15rem 0.5rem;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    background: transparent;
    color: #9aa0a6;
    font-size: 0.75rem;
    line-height: 1.6;
    transition: color 0.12s, border-color 0.12s, background 0.12s;

    &:hover:not(:disabled) {
      border-color: $mad-border;
      background: $mad-head-bg;
      color: #4a5560;
    }

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }

  /* 행 따라가기. 지표가 11개에 가로 스크롤까지 있으면 한 행을 눈으로 좇기 어렵다.
   * 계정 열은 CDataTable 이 **별도 표**로 그리는데, nth-child 로 걸면 두 표에
   * 같은 줄무늬가 생겨 자연히 맞물린다(JS 로 두 표를 동기화할 필요가 없다). */
  ::v-deep tbody tr:nth-child(even) > td {
    background: #fafbfc;
  }

  ::v-deep tbody tr:hover > td {
    background: #eef4fb;
  }

  // 값이 하나도 없는 계정 행 — 지우지 않고 뒤로 물린다(계정 순서는 모든 표에서 같아야 한다)
  ::v-deep tr.multi-account-dashboard__row--valueless {
    opacity: 0.45;

    &:hover {
      opacity: 1;
    }
  }

  // 「12:03 조회」 — 숫자가 아니라 기준 표기라 눈에 덜 띄게, 대신 항상 같은 자리에
  &__built-at {
    margin-left: auto;
    padding-left: 0.75rem;
    color: #9aa0a6;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  // CDataTable 기본 행 높이(48px)는 이 화면엔 너무 크다.
  // 계정 줄을 한 줄 40px 로 눌러 둔 걸 표 컴포넌트로 바꾸면서 되돌리지 않는다.
  &__table-wrap {
    ::v-deep .v-data-table {
      font-size: 0.85rem;

      td {
        height: 40px;
        padding: 0 0.7rem;
        white-space: nowrap;
      }

      th {
        padding: 0 0.7rem;
        white-space: nowrap;
        vertical-align: bottom;
      }

      // 헤더 글자는 컴포넌트가 안쪽 div 에 인라인 `white-space: pre-line` 을 준다.
      // 그대로 두면 한글이 **글자 단위로** 끊겨 「매입가 미입력여부 공지사항」이 12줄이 된다
      // (실측 헤더 높이 200px). buildHeaders 가 넣어둔 개행만 살리도록 pre 로 덮는다.
      .v-data-table-header th .header-with-help-vertical,
      .v-data-table-header th .header-with-help-vertical span {
        white-space: pre !important;
        line-height: 1.25;
      }
    }
  }

  &__cell {
    display: inline-block;
  }

  // 값 아래 딸리는 보조 표기(발주 필요금액 등). 값보다 작고 연하게 — 주인공이 아니다
  &__cell-sub {
    display: block;
    margin-top: -2px;
    color: #8a9099;
    font-size: 0.7rem;
    font-variant-numeric: tabular-nums;
    line-height: 1.2;
  }

  /* 직전 기간 대비. 값 **아래** 한 줄로 둔다 — 옆에 붙이면 열이 넓어져 가로 스크롤이 늘어난다.
   * ▲ 빨강 / ▼ 파랑 은 국내 증권 화면 관례다. 화면이 "좋다/나쁘다"를 판단하지 않고
   * 방향만 말한다 — 광고비가 올랐다고 나쁜 것도, 매출이 올랐다고 항상 좋은 것도 아니다. */
  &__delta {
    display: block;
    margin-top: -2px;
    font-size: 0.7rem;
    font-variant-numeric: tabular-nums;
    line-height: 1.2;

    &--up { color: #d9534f; }
    &--down { color: #3572b0; }
  }

  // 다섯 번째 상태: 해당 없음. 빗금 바탕으로 '값이 없다'와 눈에 띄게 구분한다.
  // 표 컴포넌트가 칸(td)에 클래스를 주지 않으므로, 슬롯 안에서 칸을 통째로 덮는다
  // (좌우 음수 여백 = 칸 좌우 padding 0.7rem 만큼 되밀기)
  &__cell--na {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    height: 40px;
    margin: 0 -0.7rem;
    padding: 0 0.7rem;
    background-image: repeating-linear-gradient(
      45deg,
      #f4f5f7,
      #f4f5f7 4px,
      #eceef1 4px,
      #eceef1 8px
    );
  }

  /* ── 계정별 보기 표 ────────────────────────────────────────────
   * 표 자체(가로 스크롤·계정 열 고정·행 높이)는 CDataTable 이 맡는다.
   * 여기서는 공용 컴포넌트를 고치지 않고 두 가지만 얹는다:
   *   ① 헤더 두 줄(영역/지표)이 띄어쓰기에서 또 접히지 않게 한다
   *   ② 영역이 바뀌는 첫 열에 세로 경계선
   * ──────────────────────────────────────────────────────────── */
  &__account-table {
    // 컴포넌트가 헤더에 pre-line 을 주는데, 그러면 「로켓그로스 > 상품관리」 가
    // 띄어쓰기에서 한 번 더 접혀 줄 수가 늘어난다. pre 로 덮어 두 줄로 고정한다
    ::v-deep .v-data-table-header th {
      white-space: pre !important;
    }

    ::v-deep .header-with-help-vertical,
    ::v-deep .header-with-help-vertical span {
      white-space: pre !important;
    }

    // 영역 경계 — 어디서 영역이 갈리는지 세로선으로 보인다
    ::v-deep th.mad-col-area-start,
    ::v-deep td.mad-col-area-start {
      border-left: 2px solid #cfd6e0;
    }
  }

  &__cell-na {
    display: inline-block;
    padding: 0 0.35rem;
    border: 1px dashed #c9ced6;
    border-radius: 0.25rem;
    color: #98a0aa;
    font-size: 0.72rem;
  }

  // 이름·이메일·바로가기를 한 줄로 눕힌다 (행 높이 ≈ 40px)
  &__account {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    min-width: 0;
    max-width: 100%;
  }

  &__account-name {
    flex: 0 1 auto;
    min-width: 3rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 600;
  }

  &__account-email {
    flex: 1 1 auto;
    min-width: 3rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.75rem;
    color: #888;
  }

  &__account-go {
    flex: 0 0 auto;
    width: 1.5rem;
    height: 1.5rem;
    padding: 0;
    border: 1px solid #b9d4f5;
    border-radius: 0.25rem;
    background: #fff;
    color: #2f6fb5;
    font-size: 0.8rem;
    line-height: 1;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: #eef5fd;
    }

    &:focus {
      outline: 2px solid #9ec5fe;
      outline-offset: 1px;
    }

    &:disabled {
      cursor: default;
      opacity: 0.5;
    }
  }

  // 세 상태가 눈으로 구분돼야 한다: 정상 0 / 값 없음 / 조회 실패
  // 눌러서 그 화면 필터로 가는 값(확인요청 대상). 글자는 값 그대로 두고 밑줄로만 누를 수 있음을 알린다
  &__cell-link {
    padding: 0;
    border: 0;
    background: none;
    text-decoration: underline dotted;
    cursor: pointer;

    &:disabled {
      cursor: wait;
    }
  }

  &__cell-value {
    font-variant-numeric: tabular-nums;

    &--zero {
      color: #9aa0a6;
    }

    &--alert {
      color: #d9534f;
      font-weight: 600;
    }

    // 지켜볼 값(ROI·ROAS 하락 등). 노란 글자는 흰 바탕에서 안 읽혀 짙은 호박색으로 둔다(대비 약 5:1)
    &--warn {
      color: #a35a00;
      font-weight: 600;
    }

    // 영역을 대표하는 두 숫자. 한 줄에 11개가 같은 무게로 늘어서면 어디를 봐야 할지 모른다
    &--strong {
      color: #1b2a3a;
      font-weight: 700;
      font-size: 0.95rem;
    }

    // 파생·보조(부가세 등) — 지우지 않고 한 단계 눌러 둔다
    &--muted {
      color: #8a9099;
      font-weight: 400;
    }
  }

  &__cell-empty {
    color: #c4c4c4;
  }

  &__cell-error {
    display: inline-block;
    padding: 0 0.35rem;
    border: 1px solid #f0b4b1;
    border-radius: 0.25rem;
    background: #fdecec;
    color: #b02a25;
    font-size: 0.72rem;
  }

  // 로딩 표시 — 정지된 '···' 은 '멈춤'과 구분이 안 된다. 점이 차례로 밝아지게 한다.
  &__cell-loading {
    display: inline-flex;
    align-items: center;
    gap: 0.16rem;

    i {
      width: 0.25rem;
      height: 0.25rem;
      border-radius: 50%;
      background: #c4c4c4;
      animation: multi-account-dashboard-dot 1.05s ease-in-out infinite;

      &:nth-child(2) { animation-delay: 0.15s; }
      &:nth-child(3) { animation-delay: 0.3s; }
    }
  }

  // 도는 원. 진행 줄과 영역 제목 옆에서 같이 쓴다.
  &__spinner {
    display: inline-block;
    width: 0.9rem;
    height: 0.9rem;
    border: 2px solid #d5dde8;
    border-top-color: #3f6ad8;
    border-radius: 50%;
    vertical-align: -0.15rem;
    animation: multi-account-dashboard-spin 0.7s linear infinite;

    &--sm {
      width: 0.7rem;
      height: 0.7rem;
      border-width: 1.5px;
    }
  }

  &__progress {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
    padding: 0.45rem 0.75rem;
    border: 1px solid #dfe6f1;
    border-radius: 0.35rem;
    background: #f6f9ff;
  }

  &__progress-text {
    color: #40506b;
    font-size: 0.82rem;
    white-space: nowrap;
  }

  &__progress-rest {
    color: #8a97ad;
  }

  &__progress-bar {
    flex: 1 1 auto;
    min-width: 4rem;
    height: 0.3rem;
    border-radius: 0.15rem;
    background: #e3eaf5;
    overflow: hidden;
  }

  &__progress-fill {
    display: block;
    height: 100%;
    border-radius: 0.15rem;
    background: #3f6ad8;
    transition: width 0.25s ease;
  }

  &__area-loading {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    margin-left: 0.4rem;
    color: #6b7a90;
    font-size: 0.75rem;
    font-weight: 400;
  }

  // 네 번째 상태: 수집 전. 오류(빨강)도 값 없음(연회색)도 아닌 호박색 안내로 떼어 놓는다
  &__cell-need {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.12rem 0.5rem;
    border: 1px solid #e5b25d;
    border-radius: 0.25rem;
    background: #fff5e2;
    color: #8a5a00;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1.5;

    &--action {
      cursor: pointer;

      &:hover:not(:disabled) {
        background: #ffe9c2;
      }

      &:focus {
        outline: 2px solid #d9a441;
        outline-offset: 1px;
      }

      &:disabled {
        cursor: default;
        opacity: 0.6;
      }
    }
  }
}

@keyframes multi-account-dashboard-spin {
  to { transform: rotate(360deg); }
}

@keyframes multi-account-dashboard-dot {
  0%, 80%, 100% { background: #dcdcdc; }
  40% { background: #9aa0a6; }
}

// 움직임을 줄여 달라고 설정한 사용자에게는 돌리지 않는다. 표시 자체는 남는다.
@media (prefers-reduced-motion: reduce) {
  .multi-account-dashboard__spinner,
  .multi-account-dashboard__cell-loading i {
    animation: none;
  }
}

/* ── 목업 구조(블록) 공통 ─────────────────────────────────────────
   블록 = 제목 한 줄 + 표 하나. 영역 카드를 8개 쌓던 옛 구조와 달리
   묶음 하나가 한 화면 안에서 끝난다. */
.mad-block {
  &__head {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 10px;
  }
  &__title {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 700;
    color: #1f2b3a;
  }
  &__note {
    font-size: 0.78rem;
    color: #8a93a0;
  }
}

/* 로켓그로스 리스크 타일 — 빨강(위험)/호박(주의)은 색 + 아이콘 + 글자로 가른다(색만으로 구분하지 않는다).
 * 0건 타일은 흐리게 뒤로. 고른 타일은 테두리·배경·「표 필터 중」 글자로 표시한다. */
.mad-risk {
  margin-top: 0.5rem;

  &__head {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 0.4rem;
  }
  &__legend { display: inline-flex; gap: 0.35rem; }
  &__tag {
    font-size: 0.7rem;
    font-weight: 600;
    &--alert { color: #b5413d; }
    &--warn { color: #a35a00; }
  }
  &__clear {
    padding: 0.05rem 0.55rem;
    border: 1px solid #c9ced6;
    border-radius: 999px;
    background: #fff;
    color: #3a465c;
    font-size: 0.72rem;
    &:hover { border-color: #3a465c; }
  }
  &__groups {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 0.6rem;
  }
  &__group {
    padding: 0.45rem 0.55rem;
    border: 1px solid #e3e7ec;
    border-radius: 0.4rem;
    background: #fafbfc;
  }
  &__group-head {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin-bottom: 0.35rem;
  }
  &__group-title { font-size: 0.8rem; font-weight: 700; color: #1f2b3a; }
  &__group-period { font-size: 0.7rem; color: #5f6b7a; }
  &__group-count { margin-left: auto; font-size: 0.7rem; color: #5f6b7a; }
  &__tiles {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(118px, 1fr));
    gap: 0.35rem;
  }
  &__tile {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.05rem;
    padding: 0.35rem 0.5rem;
    border: 1px solid #e3e7ec;
    border-left-width: 3px;
    border-radius: 0.3rem;
    background: #fff;
    text-align: left;
    cursor: pointer;

    &--alert { border-left-color: #d9534f; }
    &--alert &-label, &--alert &-main { color: #b5413d; }
    &--warn { border-left-color: #d98e04; }
    &--warn &-label, &--warn &-main { color: #a35a00; }

    &:hover:not(:disabled) { background: #f4f7fb; }
    &:focus-visible { outline: 2px solid #2f6bbd; outline-offset: 1px; }

    // 0건 — 흐리게. 누를 것이 없어 비활성이다
    &--zero {
      opacity: 0.5;
      cursor: default;
      .mad-risk__tile-label, .mad-risk__tile-main { color: #6b7280; }
    }
    &--active {
      border-color: #2f6bbd;
      background: #eef4fc;
      box-shadow: 0 0 0 1px #2f6bbd inset;
    }
  }
  &__tile-label { font-size: 0.72rem; font-weight: 600; line-height: 1.3; }
  &__tile-main { font-size: 0.95rem; font-weight: 800; font-variant-numeric: tabular-nums; }
  &__tile-sub { font-size: 0.7rem; color: #5f6b7a; font-variant-numeric: tabular-nums; }
  &__tile-picked { font-size: 0.66rem; font-weight: 600; color: #2f6bbd; }

  &__filter-bar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    padding: 0.3rem 0.6rem;
    border-radius: 0.3rem;
    background: #eef4fc;
    color: #1f2b3a;
    font-size: 0.78rem;
  }
}

// 리스크 타일로 고른 열 — 머리글·칸을 같이 칠해 어느 열로 좁혔는지 보인다
.mad-block ::v-deep th.mad-risk-col,
.mad-block ::v-deep td.mad-risk-col {
  background: #eef4fc !important;
}

/* 수집 현황 칩 — 상태 넷을 색이 아니라 **글자로도** 구분한다(색만으로 구분하지 않는다) */
.mad-chip {
  display: inline-block;
  min-width: 72px;
  padding: 3px 10px;
  border-radius: 11px;
  border: 1px solid transparent;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.4;
  text-align: center;
  white-space: nowrap;

  &--collected { background: #e8f6ee; border-color: #bfe3cd; color: #1d7a45; }
  &--none      { background: #fdf1e3; border-color: #f3d4ac; color: #9a5b09; }
  &--na        { background: #f2f3f5; border-color: #e2e5e9; color: #8a93a0; }
  &--error     { background: #fdecec; border-color: #f5c6c6; color: #a32b2b; }
  /* 받아뒀지만 한참 밀린 칸. 미수집(연한 주황)보다 진하게 — 같은 '할 일'이라도 이유가 다르다 */
  &--stale     { background: #fce8d0; border-color: #e9ab5c; color: #8a4b00; }
}

/* ── 매출현황(일자별) ──────────────────────────────────────────── */
.mad-sales {
  /* 일자 칸 = 펼침 버튼. 버튼처럼 보이지 않게 두되 누를 수 있다는 건 캐럿으로 알린다 */
  &__date {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0;
    border: 0;
    background: none;
    color: #1f2b3a;
    font-weight: 600;
    cursor: pointer;

    &:hover .mad-sales__date-text { text-decoration: underline; }
  }
  &__caret {
    width: 12px;
    color: #8a93a0;
    font-size: 0.7rem;
  }
  &__count {
    color: #9aa0a6;
    font-size: 0.72rem;
    font-weight: 400;
  }
  /* 계정 행 — 한 단 들여쓴다. 일자 행 밑에 딸린 것임을 들여쓰기로만 말한다 */
  &__child {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding-left: 22px;
    color: #4a5563;
  }
  &__loading {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 2px;
    color: #6c757d;
    font-size: 0.85rem;
  }
  /* 날짜 축이 없는 값(재고금액·매입가 미입력) — 표 밖에 한 줄로 */
  &__aside {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 18px;
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px dashed #e6e9ee;
    font-size: 0.78rem;
    color: #6c757d;
  }
  &__aside-item {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px 10px;
  }
  &__aside-label {
    color: #8a93a0;
    font-weight: 600;
  }
  &__aside-value {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
}

.mad-chart {
  margin-top: 14px;
  padding-top: 10px;
  border-top: 1px solid #eef1f5;

  &__title {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 2px;
    font-size: 0.85rem;
    font-weight: 700;
    color: #1f2b3a;
  }
}

/* 행 클래스는 표 컴포넌트가 <tr> 에 직접 붙이므로 BEM 중첩 밖에 둔다 */
.mad-sales-row--child > td {
  background: #fbfcfd;
}

.mad-block__spacer { flex: 1 1 auto; }

.mad-collect {
  /* 계정이 어떤 서비스를 쓰는지 — 「해당 없음」 칸들의 이유를 같은 줄에서 읽게 한다 */
  &__uses {
    margin-left: 6px;
    font-size: 0.72rem;
    color: #8a93a0;
    white-space: nowrap;
  }
  &__legend {
    margin-top: 10px;
  }
}

/* ══════════════════════════════════════════════════════════════════
   생김새 — 목업 기준
   구조는 위에서 끝났고, 여기는 "한눈에 들어오게" 만드는 층이다.
   원칙 세 가지:
     ① 블록 제목이 가장 크다. 스크롤하다 멈춰도 어느 표인지 바로 읽혀야 한다
     ② 표 머리글에 배경을 깔아 머리와 몸을 가른다. 흰 바탕에 줄만 그으면 경계가 안 잡힌다
     ③ 행을 키운다. 빽빽하면 정보가 많아 보이지만 실제로는 아무것도 안 읽힌다
   ══════════════════════════════════════════════════════════════════ */

.multi-account-dashboard {
  /* 카드 — 블록 하나가 한 덩어리로 보이게 */
  ::v-deep .card.mad-block,
  ::v-deep .mad-block.card {
    border: 1px solid #e7ecf4;
    border-radius: 14px;
    box-shadow: 0 1px 2px rgba(21, 38, 66, 0.04), 0 8px 24px rgba(21, 38, 66, 0.05);
  }
}

.mad-block {
  &__head {
    gap: 10px 14px;
    margin-bottom: 14px;
  }

  /* ① 제목 — 목업의 무게. 소제목 크기로는 블록 경계가 안 잡힌다 */
  &__title {
    font-size: 1.32rem;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: #16233c;
  }

  &__note {
    font-size: 0.8rem;
    color: #8d96a3;
  }
}

/* ② 표 — 머리글에 배경, 몸통은 여백을 키운다.
   CDataTable 안쪽이라 ::v-deep 으로 넘어간다(컴포넌트는 안 고친다). */
.multi-account-dashboard__table-wrap {
  ::v-deep table {
    thead th {
      height: auto !important;
      padding: 12px 10px !important;
      background: #f2f6fd;
      border-bottom: 1px solid #dfe7f4 !important;
      color: #34415a !important;
      font-size: 0.78rem !important;
      font-weight: 700 !important;
      letter-spacing: -0.01em;
      white-space: pre;
    }

    tbody td {
      height: auto !important;
      padding: 11px 10px !important;
      border-bottom: 1px solid #f1f4f9 !important;
      color: #3a465c;
      font-size: 0.86rem;
    }

    /* 마우스를 올린 줄만 밝게 — 계정이 여럿일 때 가로로 눈을 따라가게 */
    tbody tr:hover td { background: #f7fafe; }
  }
}

/* ③ 숫자 — 대표 두 개만 크고 진하게. 나머지는 한 단계 물러선다 */
.multi-account-dashboard__cell-value {
  font-size: 0.88rem;

  &--strong {
    font-size: 1.02rem;
    font-weight: 800;
    color: #16233c;
  }
}

/* 상태 칩 — 글자만으로 구분하지 않고 앞에 표식을 둔다(색맹 대비이기도 하다) */
.mad-chip {
  min-width: 86px;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 0.8rem;

  &::before {
    display: inline-block;
    width: 1em;
    margin-right: 4px;
    font-weight: 800;
  }

  &--collected::before { content: '✓'; }
  &--none::before      { content: '!'; }
  &--na::before        { content: '–'; }
  &--error::before     { content: '✕'; }
  &--stale::before     { content: '⚠'; }
}

/* 계정 칸 — 이름 한 줄, 그 아래 어떤 서비스를 쓰는지 한 줄.
   한 줄에 다 붙이면 이름이 잘리고 뱃지가 숫자처럼 읽힌다.
   마크업은 그대로 두고 줄만 바꾼다(flex-basis 100% = 다음 줄). */
.multi-account-dashboard__account {
  flex-wrap: wrap;
  row-gap: 2px;
}

.multi-account-dashboard__account-name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #16233c;
}

.mad-collect__uses,
.multi-account-dashboard__account-email {
  flex: 1 0 100%;
  margin-left: 1.05rem;
  font-size: 0.74rem;
  color: #9aa3b0;
  /* 이메일이 붙어 길어졌다. 계정 열은 260~280px 고정이라 넘치면 잘라 준다 */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.multi-account-dashboard__account-go {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 0.4rem;
}

/* 일자 칸 — 누를 수 있는 곳이라 캐럿을 키우고 글자를 진하게 */
.mad-sales {
  &__date { font-size: 0.92rem; }
  &__caret { width: 14px; font-size: 0.8rem; }
  &__date-text { color: #16233c; }
}

/* 펼친 계정 행 — 왼쪽에 선을 그어 '딸린 줄' 임을 말한다 */
.mad-sales-row--child > td {
  background: #f6f9fe;
}
.mad-sales-row--child > td:first-child {
  box-shadow: inset 3px 0 0 #cfe0f7;
}

/* 차트 — 표와 한 카드 안이라 경계를 연하게 두고 제목만 세운다 */
.mad-chart {
  &__title {
    font-size: 0.95rem;
    font-weight: 800;
    color: #16233c;
  }
}

/* 머리글 묶음 뱃지(상품관리 / 공급관리) — 목업만큼 또렷하게 */
.multi-account-dashboard__table-wrap ::v-deep .header-group-badge {
  margin-bottom: 3px;
  padding: 1px 8px;
  font-size: 10.5px;
  line-height: 17px;
}
</style>
